// File: src/pages/BuyPlans.jsx
import { useEffect, useState, useContext, useRef, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api, {
  getAccessToken as getApiAccessToken,
  refreshAccessToken,
} from "../api";
import { AuthContext } from "../context/AuthContext";
import { Browser } from "@capacitor/browser";
import { AppLauncher } from "@capacitor/app-launcher";
import { Capacitor } from "@capacitor/core";
import ConfirmModal from "../components/ConfirmModal";
import QRScannerModal from "../components/QRScannerModal";
import {
  buySmartTiming,
  hasSmartTiming,
  useSubTick,
  isNative,
  syncSubToBackend,
} from "@/iap";
import { Purchases } from "@revenuecat/purchases-capacitor";
import { trackPurchase, afTrack } from "../analytics/appsflyer";
import { buildSmartTimingWebPurchaseUrl } from "../webBilling";

const WEB_CHECKOUT_MAX_AGE_MS =
  2 * 60 * 60 * 1000; // 2 hours

async function waitForRevenueCatReady(timeoutMs = 10000) {
  const start = Date.now();
  if (window.__RC_READY__ === true) return;
  let resolved = false;
  const once = new Promise((resolve) => {
    const handler = () => {
      if (!resolved) {
        resolved = true;
        document.removeEventListener("rc-ready", handler);
        resolve();
      }
    };
    document.addEventListener("rc-ready", handler, { once: true });
  });
  while (window.__RC_READY__ !== true) {
    if (Date.now() - start > timeoutMs) throw new Error("Billing not ready. Please try again in a second.");
    await Promise.race([once, new Promise(r => setTimeout(r, 250))]);
    if (window.__RC_READY__ === true) break;
  }
}

function formatFiat(
  amount,
  currency = "USD",
) {
  const value = Number(amount);
  const safeCurrency = currency || "USD";

  if (!Number.isFinite(value)) {
    return "--";
  }

  try {
    return new Intl.NumberFormat(
      undefined,
      {
        style: "currency",
        currency: safeCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    ).format(value);
  } catch {
    return `${safeCurrency} ${value.toFixed(2)}`;
  }
}

// Paste this near the top of BuyPlans.jsx and Pricing.jsx (once per file)
async function ensureFreshToken() {
  try {
    return await refreshAccessToken();
  } catch (e) {
    console.warn(
      "[ensureFreshToken] refresh failed",
      e
    );

    return getApiAccessToken();
  }
}

function readCachedDisplayBalance(
  maxAgeMs = 10 * 60 * 1000,
) {
  try {
    const raw = sessionStorage.getItem(
      "strike_display_balance_v2",
    );

    if (!raw) return null;

    const {
      v,
      currency,
      ts,
    } = JSON.parse(raw);

    if (
      typeof v !== "number" ||
      !Number.isFinite(v)
    ) {
      return null;
    }

    if (
      !ts ||
      Date.now() - ts > maxAgeMs
    ) {
      return null;
    }

    return {
      value: v,
      currency: currency || null,
    };
  } catch {
    return null;
  }
}

export default function BuyPlans({
  setToken,
  userInfo,
  fetchBuyPlans,
  buyPlans,
  fetchUserInfo,
}) {
  const [buyAmount, setBuyAmount] = useState("");
  const [fiatCurrency, setFiatCurrency] = useState(null);
  const [availableFiatCurrencies, setAvailableFiatCurrencies] =
    useState([]);
  const [requiresCurrencySelection, setRequiresCurrencySelection] =
    useState(false);
  const [frequency, setFrequency] = useState("weekly");
  // Web billing applies to *all* browsers, including iPhone/Android browsers.
  // Viewport width must never determine which subscription checkout a user gets.
  const isWeb = !isNative();
  const location = useLocation();
  const smartTimingUpgradeRequested = (() => {
    const p = new URLSearchParams(location.search).get("smartTiming");
    return p === "true" || p === "1";
  })();
  useEffect(() => {
    if (!smartTimingUpgradeRequested) return;

    try {
      localStorage.removeItem(
        "satstacker_signup_intent"
      );
    } catch {}
  }, [smartTimingUpgradeRequested]);
  const initialSmartTiming = (() => {
    const p = new URLSearchParams(location.search).get("smartTiming");
    const requested = p === "true" || p === "1";

    // Explicit Smart Timing upsell links should win.
    if (requested) {
      return true;
    }

    // Otherwise preserve the existing plan state.
    if (buyPlans?.length) {
      return !!buyPlans[0].smart_timing;
    }

    return false;
  })();
  const [smartTiming, setSmartTiming] = useState(initialSmartTiming);
  const [payoutAddr, setPayoutAddr] = useState("");
  const initialCachedBalance =
    readCachedDisplayBalance(
      10 * 60 * 1000,
    );

  const [fiatAvailable, setFiatAvailable] =
    useState(
      initialCachedBalance?.value ?? null,
    );

  const [editMode, setEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [originalPlan, setOriginalPlan] = useState(null);

  const [message, setMessage] = useState("");
  const auth = useContext(AuthContext); // (ok if unused)
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [destination, setDestination] = useState("strike");

  const cleanedAddr   = extractAddress(payoutAddr).trim();
  const addressValid  = isValidBitcoinAddress(cleanedAddr);
  const addressMissing = cleanedAddr.length === 0;
  const addrBlocker   = destination === "onchain" && (addressMissing || !addressValid);
  const navigate = useNavigate();

  const amountTooLow = parseFloat(buyAmount || 0) < 5;
  const [showEmailNotice, setShowEmailNotice] = useState(false);
  const [emailNoticeText, setEmailNoticeText] = useState("");
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const updateInFlightRef = useRef(false);
  const [amountTouched, setAmountTouched] = useState(false);
  const subTick = useSubTick();
  const isAndroid = /Android/i.test(navigator.userAgent);
  void subTick;
  const isIOS =
    Capacitor.isNativePlatform() &&
    Capacitor.getPlatform() === "ios";

  const [iosIntroEligibility, setIosIntroEligibility] =
    useState("unknown");
  // "eligible" | "ineligible" | "unknown"
  const subscribeErrorShownRef = useRef(false);
  const userDisabledSmartTimingRef = useRef(false);
  const [recentSmartTimingWin, setRecentSmartTimingWin] = useState(null);
  const [strikeQuestion, setStrikeQuestion] = useState(() => {
    try { return localStorage.getItem("strike_has_account") || null; } catch { return null; }
  });
  const [referralEligible, setReferralEligible] = useState(false);
  const [referralOfferLoaded, setReferralOfferLoaded] = useState(false);

  // Keeps the calculator win visible on the plan page after we consume calc_prefill.
  // This is the key context that makes Smart Timing feel like the product, not an add-on.
  const [calcEstimate, setCalcEstimate] = useState(() => {
      try {
        const raw = localStorage.getItem("calc_prefill");
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    });

    useEffect(() => {
    if (
      !fiatCurrency &&
      initialCachedBalance?.currency
    ) {
      setFiatCurrency(
        initialCachedBalance.currency,
      );
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("calc_prefill");
      if (!raw) return;
      const prefill = JSON.parse(raw);
      const { amount, freq } = prefill;
      if (amount) setBuyAmount(String(amount));
      if (freq) setFrequency(freq);
      setCalcEstimate(prefill);
      localStorage.removeItem("calc_prefill");
    } catch {}
  }, []);

  useEffect(() => {
    if (
      !fiatCurrency &&
      availableFiatCurrencies.length === 1
    ) {
      setFiatCurrency(
        availableFiatCurrencies[0],
      );
      setRequiresCurrencySelection(false);
    }
  }, [
    fiatCurrency,
    availableFiatCurrencies,
  ]);

  const [serverSub, setServerSub] = useState(() => {
    // 1) Try userInfo first (available on warm navigations)
    if (userInfo?.is_premium != null) {
      return {
        active: !!userInfo.is_premium,
        is_premium: !!userInfo.is_premium,
        sub_expiry: userInfo.sub_expiry ?? null,
        provider: userInfo.subscription_provider ?? null,
      };
    }

    // 2) Fall back to localStorage cache
    try {
      const raw = localStorage.getItem("ss_sub_cache");

      if (raw) {
        const {
          active,
          is_premium,
          sub_expiry,
          provider,
        } = JSON.parse(raw);

        return {
          active: !!active,
          is_premium: !!is_premium,
          sub_expiry: sub_expiry ?? null,
          provider: provider ?? null,
        };
      }
    } catch {}

    return {
      active: false,
      is_premium: false,
      sub_expiry: null,
      provider: null,
    };
  });

  // Cache-writing wrapper -- keeps localStorage in sync so the next mount seeds correctly
  const setServerSubCached = (val) => {
    try { localStorage.setItem("ss_sub_cache", JSON.stringify(val)); } catch {}
    setServerSub(val);
  };

  useEffect(() => {
    if (!isIOS || serverSub.active) {
      setIosIntroEligibility("unknown");
      return;
    }

    // Wait until we know whether this customer
    // should use the referral offering.
    if (!referralOfferLoaded) {
      return;
    }

    let cancelled = false;

    const checkEligibility = async () => {
      try {
        await waitForRevenueCatReady(10000);

        const offerings =
          await Purchases.getOfferings();

        const selectedOffering =
          referralEligible
            ? offerings?.all?.["smart_timing_referral"]
            : offerings?.current;

        const pkg =
          selectedOffering?.availablePackages?.find(
            (p) => p?.identifier === "$rc_monthly"
          ) ||
          selectedOffering?.availablePackages?.[0];

        const productId =
          pkg?.product?.identifier;

        if (!productId) {
          throw new Error(
            "Could not determine iOS subscription product."
          );
        }

        const result =
          await Purchases.checkTrialOrIntroductoryPriceEligibility({
            productIdentifiers: [productId],
          });

        if (cancelled) return;

        const status =
          result?.[productId]?.status;

        // RevenueCat:
        // 0 = unknown
        // 1 = ineligible
        // 2 = eligible
        // 3 = no intro offer
        if (status === 2) {
          setIosIntroEligibility("eligible");
        } else if (
          status === 1 ||
          status === 3
        ) {
          setIosIntroEligibility("ineligible");
        } else {
          setIosIntroEligibility("unknown");
        }
      } catch (err) {
        console.warn(
          "[Billing] Could not determine iOS trial eligibility",
          err
        );

        if (!cancelled) {
          setIosIntroEligibility("unknown");
        }
      }
    };

    checkEligibility();

    return () => {
      cancelled = true;
    };
  }, [
    isIOS,
    serverSub.active,
    userInfo?.id,
    referralEligible,
    referralOfferLoaded,
  ]);

  const [strikeStatus, setStrikeStatus] = useState(() => {
    try {
      const raw = sessionStorage.getItem("strike_status_cache");
      if (raw) return JSON.parse(raw);
    } catch {}
    return { linked: false, auto_refresh: false };
  });
  const [strikeConnecting, setStrikeConnecting] = useState(false);
  const [strikeRetryPrompt, setStrikeRetryPrompt] = useState(false);
  const apiBase = import.meta.env.VITE_API_BASE_URL || "https://api.satstacker.app";
  useEffect(() => {
    let cancelled = false;

    async function loadRecentSmartTimingWin() {
      try {
        const res = await api.get("/smart-timing/highlights");

        if (!cancelled) {
          // Public examples must be completed windows using the same
          // scheduled-DCA benchmark as the customer dashboards.
          const highlights = Array.isArray(res.data?.highlights)
            ? res.data.highlights
            : [];
          const example = highlights.find(
            (item) =>
              item?.benchmark === "scheduled_window_end" &&
              Number.isFinite(Number(item?.more_btc_pct)) &&
              Number(item.more_btc_pct) > 0 &&
              Number.isFinite(Number(item?.extra_sats)) &&
              Number(item.extra_sats) > 0 &&
              Number.isFinite(Number(item?.smart_sats)) &&
              Number(item.smart_sats) > 0 &&
              Number.isFinite(Number(item?.scheduled_sats)) &&
              Number(item.scheduled_sats) > 0
          );
          setRecentSmartTimingWin(example || null);
        }
      } catch (err) {
        console.warn(
          "[BuyPlans] recent Smart Timing win unavailable",
          err
        );

        if (!cancelled) {
          setRecentSmartTimingWin(null);
        }
      }
    }

    loadRecentSmartTimingWin();

    return () => {
      cancelled = true;
    };
  }, []);
  const { token: ctxToken } = useContext(AuthContext);

  function getAccessToken() {
    return (
      getApiAccessToken() ||
      (typeof ctxToken === "string"
        ? ctxToken
        : "")
    );
  }

  useEffect(() => {
    if (!userInfo?.id) return;

    let cancelled = false;

    const loadReferralEligibility = async () => {
      try {
        const token =
          getAccessToken()

        if (!token) return;

        const res = await api.get("/me/referral-offer", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!cancelled) {
          setReferralEligible(
            res.data?.referral_eligible === true
          );
        }
      } catch (err) {
        console.warn(
          "[BuyPlans] referral eligibility unavailable",
          err
        );

        if (!cancelled) {
          setReferralEligible(false);
        }
      } finally {
        if (!cancelled) {
          setReferralOfferLoaded(true);
        }
      }
    };

    loadReferralEligibility();

    return () => {
      cancelled = true;
    };
  }, [userInfo?.id]);

  async function handleConnectStrike() {
    try {
      afTrack("strike_link_started", { entry: "plans" });
      try {
        try {
          await refreshAccessToken();
        } catch {}
      } catch {}
      const access = getAccessToken();
      if (!access) {
        localStorage.setItem("postLoginAction", "connectStrike");
        window.location.href = "/login";
        return;
      }
      const ltRes = await fetch(`${apiBase}/oauth/strike/link-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${access}` },
        credentials: "include",
      });
      if (!ltRes.ok) throw new Error(`link-token failed (${ltRes.status})`);
      const { token: linkToken } = await ltRes.json();
      if (!linkToken) throw new Error("No link token");
      afTrack("strike_oauth_launched", { entry: "plans" });
      const url = `${apiBase}/oauth/strike/login?t=${encodeURIComponent(linkToken)}`;
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url });
      } else {
        window.location.href = url;
      }
    } catch (e) {
      console.error(e);
      setMessage("Could not start Strike connection. Please try again.");
    }
  }

  const openStrikeStore = async () => {
    afTrack("strike_store_clicked", { entry: "plans" });
    try {
      if (Capacitor.isNativePlatform()) {
        if (isAndroid) {
          // Directly open market:// — skip canOpenUrl which always returns false for URI schemes
          try {
            await AppLauncher.openUrl({ url: "market://details?id=zapsolutions.strike" });
          } catch {
            // market:// not available (emulator etc), fall back to native browser
            await AppLauncher.openUrl({ url: "https://play.google.com/store/apps/details?id=zapsolutions.strike" });
          }
        } else {
          // iOS: directly open App Store via itms-apps scheme
          try {
            await AppLauncher.openUrl({ url: "itms-apps://itunes.apple.com/app/id1488724463" });
          } catch {
            await AppLauncher.openUrl({ url: "https://apps.apple.com/us/app/strike-bitcoin/id1488724463" });
          }
        }
      } else {
        const url = isAndroid
          ? "https://play.google.com/store/apps/details?id=zapsolutions.strike"
          : "https://apps.apple.com/us/app/strike-bitcoin/id1488724463";
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch {
      const url = isAndroid
        ? "https://play.google.com/store/apps/details?id=zapsolutions.strike"
        : "https://apps.apple.com/us/app/strike-bitcoin/id1488724463";
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const [showStrikeExplainer, setShowStrikeExplainer] = useState(false);
  const [showStrikeLinkWarning, setShowStrikeLinkWarning] = useState(false);

  // 1) Start with the cached number for instant render
  const [displayBalance, setDisplayBalance] =
    useState(() => {
      const cached = readCachedDisplayBalance();
      return cached?.value ?? null;
    });

  // 2) Update if Dashboard refreshes it while you’re on this page
  useEffect(() => {
    const onUpdate = () => {
      const cached = readCachedDisplayBalance();

      if (
        cached &&
        typeof cached.value === "number"
      ) {
        setDisplayBalance(cached.value);

        if (
          !fiatCurrency &&
          cached.currency
        ) {
          setFiatCurrency(
            cached.currency,
          );
        }
      }
    };

    window.addEventListener(
      "strike-balance-updated",
      onUpdate,
    );

    return () => {
      window.removeEventListener(
        "strike-balance-updated",
        onUpdate,
      );
    };
  }, [fiatCurrency]);

  const funnelIdRef = useRef(crypto.randomUUID());
  const funnelId = funnelIdRef.current;

  const planContext = useMemo(
    () => ({
      funnel_id: funnelId,
      page: "plans",
      mode: buyPlans.length
        ? "manage"
        : "create",
      cadence: frequency,

      amount_fiat:
        Number(buyAmount || 0),

      fiat_currency:
        fiatCurrency || null,

      ...(fiatCurrency === "USD"
        ? {
            amount_usd:
              Number(buyAmount || 0),
          }
        : {}),

      destination,
      smart_timing_selected:
        !!smartTiming,
      smart_timing_subscribed:
        !!serverSub?.active,
      platform: isNative()
        ? "native"
        : "web",
    }),
    [
      buyPlans.length,
      frequency,
      buyAmount,
      fiatCurrency,
      destination,
      smartTiming,
      serverSub?.active,
    ],
  );

  useEffect(() => {
    afTrack("buy_plans_viewed", {
      funnel_id: funnelId,
      page: "plans",
      platform: isNative() ? "native" : "web",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NEW: small epsilon for comparison and a computed "needed" amount for this plan
  const EPS = 0.005;
  const neededFiat = useMemo(() => {
    const num = Number(
      buyAmount || 0,
    );

    return Number.isFinite(num)
      ? Math.max(0, num)
      : 0;
  }, [buyAmount]);

  const strikeReady = strikeStatus.linked && strikeStatus.auto_refresh;

  const subscribedLocal = hasSmartTiming(); // device-only (iOS/Android)
  const subscribedServer = serverSub.active;  

  const openManageSubscriptions = async () => {
    if (!isNative()) {
      try {
        try {
          await refreshAccessToken();
        } catch {}

        const { data } = await api.get(
          "/api/subscriptions/revenuecat/management-url"
        );

        const url =
          data?.management_url;

        if (!url) {
          throw new Error(
            "No subscription management link was returned."
          );
        }

        window.location.assign(url);
      } catch (e) {
        console.error(
          "[Billing] could not open subscription manager",
          e
        );

        setMessage(
          "We couldn't open subscription management. Please try again."
        );
      }

      return;
    }

    const ua =
      (navigator?.userAgent || "")
        .toLowerCase();

    if (ua.includes("android")) {
      window.open(
        "https://play.google.com/store/account/subscriptions",
        "_blank"
      );
    } else {
      window.open(
        "https://apps.apple.com/account/subscriptions",
        "_blank"
      );
    }
  };

  const autoEnableTriedRef = useRef(false);
  const [showDisabledReason, setShowDisabledReason] = useState(false);
  const [disabledReasonText, setDisabledReasonText] = useState("");
  const [activating, setActivating] = useState(false);
  const [showActivated, setShowActivated] = useState(false);
  const [showRecentResult, setShowRecentResult] = useState(false);
  const suppressAutoEnableRef = useRef(false);
  const [serverPending, setServerPending] = useState(false); // set from your GET /buyplans load
  const [
    recoverableWebCheckout,
    setRecoverableWebCheckout,
  ] = useState(null);

  useEffect(() => {
    if (!isWeb) return;
    if (!userInfo?.id) return;

    const params =
      new URLSearchParams(location.search);

    // The existing success-return effect handles
    // this case itself.
    if (
      params.get("webPurchase") ===
      "success"
    ) {
      return;
    }

    let cancelled = false;

    async function loadPendingCheckout() {
      try {
        const res = await api.get(
          "/me/smart-timing-checkout-pending"
        );

        if (cancelled) return;

        setRecoverableWebCheckout(
          res?.data?.pending || null
        );
      } catch (e) {
        console.warn(
          "[BuyPlans] could not load pending Smart Timing setup",
          e
        );

        if (!cancelled) {
          setRecoverableWebCheckout(null);
        }
      }
    }

    loadPendingCheckout();

    return () => {
      cancelled = true;
    };
  }, [
    isWeb,
    userInfo?.id,
    location.search,
  ]);

  const hasPendingFlag = () => {
    const params = new URLSearchParams(location.search);
    return params.get("pending") === "1";
  };

  function isValidBitcoinAddress(address) {
    return /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/.test(address);
  }

  function extractAddress(raw) {
    if (!raw) return "";
    return raw.replace(/^bitcoin:/i, "").split("?")[0];
  }

  // Generic "call then retry once on 401" wrapper
  async function withAuthRetry(fn) {
    try {
      return await fn();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        // try refresh once
        const ok = await ensureFreshToken();
        if (!ok) throw e; // still no token: bubble up, app can route to login
        // retry once with the new token
        return await fn(true);
      }
      throw e;
    }
  }

  /* ──────────────────────────────────────────────────────────
     initial fetch & prefill
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (userInfo?.id) fetchBuyPlans();
  }, [userInfo]);

  useEffect(() => {
    const onFocus = () => {
      try {
        const raw = localStorage.getItem("pendingPlanConfirm");
        if (!raw) return;
        const { kind, ts } = JSON.parse(raw);
        const fresh = !ts || (Date.now() - ts) < 24 * 60 * 60 * 1000;
        // only resurface if URL says pending OR server shows pending
        const serverPending = buyPlans?.[0]?.status === "pending_confirmation";
        if (fresh && (hasPendingFlag() || serverPending)) {
          showEmail(kind, null);
        }
      } catch {}
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [buyPlans, location.search]);

  useEffect(() => {
    // If we didn’t get a number from cache (or it’s stale), try again with a looser TTL (e.g. 10 min)
    if (typeof fiatAvailable !== "number") {
      const cached =
        readCachedDisplayBalance(
          10 * 60 * 1000,
        );

      if (
        cached &&
        typeof cached.value === "number"
      ) {
        setFiatAvailable(cached.value);

        if (
          !fiatCurrency &&
          cached.currency
        ) {
          setFiatCurrency(
            cached.currency,
          );
        }
      }
    }
  }, []);

  useEffect(() => {
    if (buyPlans.length) {
      const p = buyPlans[0];
      setBuyAmount(
        String(
          p.amount_fiat ??
          p.amount_usd ??
          "",
        ),
      );

      setFiatCurrency(
        p.fiat_currency || null,
      );
      setFrequency(p.freq);
      const upgradeRequested =
        new URLSearchParams(location.search).get("smartTiming") === "true" ||
        new URLSearchParams(location.search).get("smartTiming") === "1";

      setSmartTiming(
        upgradeRequested && !p.smart_timing
          ? true
          : !!p.smart_timing
      );
      setOriginalPlan(p);
      setPayoutAddr(p.payout_address || "");
      setServerPending(p.status === "pending_confirmation");

      if (upgradeRequested && !p.smart_timing) {
        // User explicitly came from a Smart Timing upgrade CTA.
        setEditMode(true);
        setPendingChanges(true);
        userDisabledSmartTimingRef.current = false;
      } else if (!p.smart_timing) {
        // Normal Standard-plan visit: respect the saved plan state.
        userDisabledSmartTimingRef.current = true;
      } else {
        userDisabledSmartTimingRef.current = false;
      }
    }
  }, [buyPlans, userInfo, location.search, isWeb]);

  useEffect(() => {
    if (!buyPlans.length && userInfo?.payout_address) {
      setPayoutAddr(userInfo.payout_address);
    }
  }, [userInfo, buyPlans]);

  useEffect(() => {
    if (buyPlans.length) {
      const p = buyPlans[0];
      setDestination(p.destination || "strike");
      setPayoutAddr(p.payout_address || "");
    }
  }, [buyPlans]);


  // ─ Auto-enable when sub becomes active but plan.smart_timing=false (robust version)
  useEffect(() => {
    if (
      suppressAutoEnableRef.current ||
      userDisabledSmartTimingRef.current ||
      serverPending ||
      !subscribedServer || 
      !originalPlan || 
      originalPlan.smart_timing || 
      autoEnableTriedRef.current
    )
      return;
    autoEnableTriedRef.current = true;

    (async () => {
      try {
        await withAuthRetry(async (retried) => {
          if (!retried) await ensureFreshToken();
          const token = getAccessToken();
          return api.patch(
            `/buyplans/${originalPlan.id}`,
            {
              fiat_currency: fiatCurrency,

              amount_fiat:
                parseFloat(buyAmount),

              ...(fiatCurrency === "USD"
                ? {
                    amount_usd:
                      parseFloat(buyAmount),
                  }
                : {}),

              freq: frequency,
              smart_timing: true,
              destination,

              payout_address:
                destination === "onchain"
                  ? extractAddress(
                      payoutAddr,
                    ).trim()
                  : null,
            },
            { headers: { 
              Authorization: `Bearer ${token}`,
              "X-Idempotency-Key": crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
            } }
          );
        });

        setSmartTiming(true);
        setOriginalPlan((p) => ({ ...p, smart_timing: true }));
        setPendingChanges(false);
        await fetchBuyPlans();
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.message || "";

        if (status === 403) {
          try {
            await syncSubToBackend();
            await withAuthRetry(async (retried) => {
              if (!retried) await ensureFreshToken();
              const token = getAccessToken();
              return api.patch(
                `/buyplans/${originalPlan.id}`,
                {
                  fiat_currency: fiatCurrency,

                  amount_fiat:
                    parseFloat(buyAmount),

                  ...(fiatCurrency === "USD"
                    ? {
                        amount_usd:
                          parseFloat(buyAmount),
                      }
                    : {}),

                  freq: frequency,
                  smart_timing: true,
                  destination,

                  payout_address:
                    destination === "onchain"
                      ? extractAddress(
                          payoutAddr,
                        ).trim()
                      : null,
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            });
            setSmartTiming(true);
            setOriginalPlan((p) => ({ ...p, smart_timing: true }));
            setPendingChanges(false);
            await fetchBuyPlans();
            return;
          } catch {}
        }

        if (status === 409) {
          showEmail("edit", detail || "Check your email to confirm the plan change.");
          navigate("/plans?pending=1", { replace: true });
          return;
        }

        setMessage(detail || "Failed to enable Smart Timing.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribedServer, originalPlan?.id]);

  useEffect(() => {
    (async () => {
      try {
        const token = getAccessToken();
        if (!token || !userInfo?.id) return;
        const res = await api.get("/oauth/strike/status", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const newStatus = {
          linked: !!res.data?.linked,
          auto_refresh: !!res.data?.auto_refresh,
        };
        try { sessionStorage.setItem("strike_status_cache", JSON.stringify(newStatus)); } catch {}
        setStrikeStatus(newStatus);
      } catch {
        setStrikeStatus({ linked: false, auto_refresh: false });
      }
    })();
  }, [userInfo?.id]);

  // Re-check Strike status when returning from in-app browser (OAuth flow)
  useEffect(() => {
    let resumeSub, browserCloseSub;
    const onReturn = async () => {
      setStrikeConnecting(true);
      setStrikeRetryPrompt(false);
      let linked = false;
      for (let i = 0; i < 3; i++) {
        await new Promise(r => setTimeout(r, i === 0 ? 800 : 1500));
        try {
          const token = getAccessToken();
          const res = await api.get("/oauth/strike/status", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data?.linked) {
            setStrikeStatus({ linked: true, auto_refresh: !!res.data?.auto_refresh });
            linked = true;
            break;
          }
        } catch {}
      }
      setStrikeConnecting(false);
      if (!linked) setStrikeRetryPrompt(true);
    };
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        resumeSub = await App.addListener("resume", onReturn);
        browserCloseSub = await Browser.addListener("browserFinished", onReturn);
      } catch {}
    })();
    return () => {
      resumeSub?.remove?.();
      browserCloseSub?.remove?.();
    };
  }, [userInfo?.id]);

  useEffect(() => {
    let cancelled = false;

    async function loadBuyingPower() {
      if (!userInfo?.id || !strikeStatus.linked) return;

      try {
        const token = getAccessToken();
        if (!token) return;

        const preferredCurrency =
          buyPlans?.[0]?.fiat_currency ||
          fiatCurrency ||
          null;

        const { data } = await api.get(
          "/me/strike/buying-power",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: preferredCurrency
              ? { currency: preferredCurrency }
              : {},
          },
        );

        if (cancelled) return;

        const currencies = Array.isArray(
          data?.availableCurrencies,
        )
          ? data.availableCurrencies
          : [];

        setAvailableFiatCurrencies(currencies);
        setRequiresCurrencySelection(
          data?.requiresCurrencySelection === true,
        );

        if (data?.currency) {
          setFiatCurrency(data.currency);
        }

        const rawBuyingPower =
          data?.buying_power ??
          data?.buying_power_usd;

        const buyingPower = Number(rawBuyingPower);

        if (
          rawBuyingPower != null &&
          Number.isFinite(buyingPower)
        ) {
          const rounded = Number(
            buyingPower.toFixed(2),
          );

          sessionStorage.setItem(
            "strike_display_balance_v2",
            JSON.stringify({
              v: rounded,
              currency: data?.currency ?? null,
              ts: Date.now(),
            }),
          );

          setDisplayBalance(rounded);

          window.dispatchEvent(
            new Event("strike-balance-updated"),
          );
        }
      } catch (error) {
        console.warn(
          "Could not load Strike buying power",
          error,
        );
      }
    }

    loadBuyingPower();

    return () => {
      cancelled = true;
    };
  }, [
    userInfo?.id,
    strikeStatus.linked,
    buyPlans,
  ]);

  useEffect(() => {
    if (showEmailNotice) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showEmailNotice]);

  useEffect(() => {
    if (!activating) return;

    // Warn on refresh/close
    const beforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);

    // Android hardware back (Capacitor)
    let removeBack = null;
    try {
      removeBack = window?.Capacitor?.App?.addListener("backButton", (ev) => {
        // swallow back during activation
      });
    } catch (_) {}

    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      if (removeBack?.remove) removeBack.remove();
    };
  }, [activating]);

  useEffect(() => {
    if (!serverSub.active) {
      // Explicit Smart Timing upsell links are allowed to preselect
      // Smart Timing on an existing Standard plan.
      if (
        originalPlan &&
        !originalPlan.smart_timing &&
        smartTiming &&
        !smartTimingUpgradeRequested
      ) {
        setSmartTiming(false);
      }
    }
  }, [
    serverSub.active,
    originalPlan?.smart_timing,
    smartTimingUpgradeRequested,
  ]);

  /* ──────────────────────────────────────────────────────────
     helpers
  ────────────────────────────────────────────────────────── */
  const saveAddress = async () => {
    try {
      const clean = extractAddress(payoutAddr).trim();
      if (!clean) throw new Error("Deposit address is required");
      if (!isValidBitcoinAddress(clean)) throw new Error("Invalid Bitcoin address");

      await withAuthRetry(async (retried) => {
        if (!retried) await ensureFreshToken();
        const token = getAccessToken();
        return api.post(
          "/users/set-deposit-address",
          { address: clean },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });

      setMessage("Deposit address saved.");
      return true;
    } catch (err) {
      setMessage(err?.response?.data?.detail || err.message || "Failed to save address.");
      return false;
    }
  };

  const handleCreatePlan = async () => {
    // Do not send a customer to paid checkout with an incomplete funding choice.
    if (strikeStatus.linked && requiresCurrencySelection && !fiatCurrency) {
      setMessage("Choose a funding currency before starting your trial.");
      return;
    }

    // Validate destination before asking them to subscribe.
    if (destination === "onchain") {
      const clean = extractAddress(payoutAddr).trim();

      if (!clean) {
        setMessage("Deposit address is required");
        return;
      }

      if (!isValidBitcoinAddress(clean)) {
        setMessage("Invalid Bitcoin address");
        return;
      }
    }

    const needsWebSmartTimingTrial =
      smartTiming &&
      !serverSub.active &&
      isWeb;

    const needsSmartTimingTrial =
      smartTiming &&
      !serverSub.active &&
      isNative();

    if (needsSmartTimingTrial) {
      afTrack("smart_timing_final_cta_clicked", {
        ...planContext,
        entry: "create_plan_cta",
        trial: nativeTrialExpected,
        price_after_trial: 1.99,
      });

      const subscriptionReady = await subscribeAndSync({
        showSuccessModal: false,
      });

      if (!subscriptionReady) {
        return;
      }
    }

    if (
      needsWebSmartTimingTrial
    ) {
      afTrack(
        "smart_timing_final_cta_clicked",
        {
          ...planContext,
          entry: "create_plan_cta",
          platform: "web",
          trial: true,
          price_after_trial: 1.99,
        }
      );

      await startWebCheckout({
        kind: "create",
        entry: "create_plan_cta",
      });

      return;
    }

    const tok =
      getAccessToken()

    if (tok) {
      await fetchUserInfo(tok);
    }

    afTrack("buy_plan_create_intent", {
      ...planContext,
      entry: "create_button",
      smart_timing_selected: !!smartTiming,
      started_trial_from_cta:
        needsSmartTimingTrial &&
        nativeTrialExpected,
    });

    // The CTA already explicitly said "& create plan",
    // so don't ask for another confirmation after the store purchase.
    if (needsSmartTimingTrial) {
      setPendingAction({
        type: "create",
        data: {
          amount: buyAmount,
          frequency,
        },
      });

      await actuallyCreatePlan();
      return;
    }

    // Preserve existing confirmation flow for standard plans
    // and already-subscribed Smart Timing users.
    setPendingAction({
      type: "create",
      data: {
        amount: buyAmount,
        frequency,
      },
    });

    setShowConfirm(true);
  };

  async function actuallyCreatePlan() {
    setSaving(true);
    try {
      if (destination === "onchain") {
        const saved = await saveAddress();
        if (!saved) return;
      }

      if (strikeStatus.linked && requiresCurrencySelection && !fiatCurrency) {
        setMessage("Choose a funding currency.");
        return;
      }

      const amount = parseFloat(buyAmount);

      const payload = {
        user_id: userInfo.id,

        ...(fiatCurrency
          ? { fiat_currency: fiatCurrency }
          : {}),

        amount_fiat: amount,

        ...(fiatCurrency === "USD"
          ? { amount_usd: amount }
          : {}),

        freq: frequency,
        smart_timing: smartTiming,
        destination,

        payout_address:
          destination === "onchain"
            ? extractAddress(payoutAddr).trim()
            : null,
      };

      // Refresh before first attempt; retry once on 401 inside withAuthRetry
      const res = await withAuthRetry(async (retried) => {
        if (!retried) {
          await ensureFreshToken();
        }

        return api.post("/buyplans", payload);
      });

      setMessage("Plan created!");
      setShowConfirm(false);
      try { await fetchBuyPlans(); } catch {}
      await afTrack("buy_plan_created", {
        ...planContext,
        backend: "success",
      });
    } catch (e) {
      const status = e?.response?.status;

      // If Smart Timing blocked, try sync then one more attempt
      if (status === 403 && smartTiming) {
        try {
          await syncSubToBackend();
          await withAuthRetry(async (retried) => {
            if (!retried) await ensureFreshToken();
            const token = getAccessToken();
            return api.post(
              "/buyplans",
              {
                user_id: userInfo.id,

                ...(fiatCurrency
                  ? { fiat_currency: fiatCurrency }
                  : {}),

                amount_fiat: parseFloat(buyAmount),

                ...(fiatCurrency === "USD"
                  ? { amount_usd: parseFloat(buyAmount) }
                  : {}),

                freq: frequency,
                smart_timing: smartTiming,
                destination,

                payout_address:
                  destination === "onchain"
                    ? extractAddress(payoutAddr).trim()
                    : null,
              },
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`,
                },
              },
            );
          });
          setMessage("Plan created!");
          setShowConfirm(false);
          try { await fetchBuyPlans(); } catch {}
          afTrack("buy_plan_created", {
            ...planContext,
            backend: "success",
          });
          return;
        } catch (e2) {
          setMessage(e2?.response?.data?.detail || "Smart Timing requires an active subscription.");
          afTrack("buy_plan_create_failed", {
            ...planContext,
            backend: "error",
            status: e?.response?.status ?? null,
            reason: e?.response?.data?.detail ?? e?.message ?? "unknown",
          });
          return;
        }
      }

      // Optional: if backend may require email confirm for *creation*, treat 409 similarly to updates
      if (status === 409) {
        showEmail("edit", e?.response?.data?.detail || "Check your email to confirm the plan.");
        navigate("/plans?pending=1", { replace: true });
        return;
      }

      setMessage(e?.response?.data?.detail || e.message || "Failed to create plan.");
    } finally {
      setSaving(false);
    }
  }

  async function actuallyUpdatePlan() {
    // Synchronous double-submit guard.
    // Unlike React state, this changes immediately.
    if (updateInFlightRef.current) {
      return;
    }

    updateInFlightRef.current = true;
    setSaving(true);
    suppressAutoEnableRef.current = true;
    setShowConfirm(false);

    showEmail(
      "edit",
      "Check your email to confirm this change."
    );

    const planId = buyPlans?.[0]?.id;

    if (!planId) {
      setShowEmailNotice(false);
      clearPendingEmail();
      setMessage("Buy plan not found.");
      updateInFlightRef.current = false;
      setSaving(false);
      return;
    }

    const updated = {
      fiat_currency: fiatCurrency,

      amount_fiat: parseFloat(
        buyAmount
      ),

      // Legacy compatibility for existing USD plans.
      ...(fiatCurrency === "USD"
        ? {
            amount_usd: parseFloat(
              buyAmount
            ),
          }
        : {}),

      freq: frequency,
      smart_timing: smartTiming,
      destination,

      payout_address:
        destination === "onchain"
          ? extractAddress(
              payoutAddr
            ).trim()
          : null,
    };

    // One key for this logical submission.
    // IMPORTANT: reuse this SAME key for authentication/subscription retries.
    const idemKey =
      crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random()}`;

    const submitUpdate = async () => {
      return withAuthRetry(
        async (retried) => {
          if (!retried) {
            await ensureFreshToken();
          }

          const token =
            getAccessToken()

          return api.patch(
            `/buyplans/${planId}`,
            updated,
            {
              headers: {
                "X-Idempotency-Key": idemKey,
              },
            }
          );
        }
      );
    };

    const handleSuccess = async (res) => {
      const requiresConfirmation =
        res?.status === 202 ||
        res?.data?.email_sent === true ||
        res?.data?.requires_confirmation === true ||
        res?.data?.status ===
          "pending_confirmation";

      if (requiresConfirmation) {
        setServerPending(true);
        setMessage("");
        setShowConfirm(false);
        setEditMode(false);
        setPendingChanges(false);
        setAmountTouched(false);

        // Replace the optimistic message with whatever
        // the server actually says.
        setShowEmailNotice(false);
        clearPendingEmail();

        showEmail(
          "edit",
          res?.data?.detail ||
            "Check your email to confirm this change."
        );

        navigate(
          "/plans?pending=1",
          { replace: true }
        );

        return;
      }

      // Non-confirmation success path.
      setShowEmailNotice(false);
      clearPendingEmail();

      setMessage("Changes saved!");
      setShowConfirm(false);
      setEditMode(false);
      setPendingChanges(false);
      setAmountTouched(false);

      try {
        await fetchBuyPlans();
      } catch {}
    };

    try {
      try {
        const res = await submitUpdate();
        await handleSuccess(res);
        return;

      } catch (err) {
        const status =
          err?.response?.status;

        // Preserve your existing Smart Timing recovery:
        // sync subscription state and retry ONCE.
        //
        // Crucially, submitUpdate() reuses idemKey.
        if (
          status === 403 &&
          smartTiming
        ) {
          try {
            await syncSubToBackend();

            const retryRes =
              await submitUpdate();

            await handleSuccess(
              retryRes
            );

            return;

          } catch (retryErr) {
            setShowEmailNotice(false);
            clearPendingEmail();

            setMessage(
              retryErr?.response?.data?.detail ||
                "Smart Timing requires an active subscription."
            );

            return;
          }
        }

        setShowEmailNotice(false);
        clearPendingEmail();

        setMessage(
          err?.response?.data?.detail ||
            err?.message ||
            "Failed to update plan."
        );
      }

    } finally {
      updateInFlightRef.current = false;
      setSaving(false);

      setTimeout(() => {
        suppressAutoEnableRef.current =
          false;
      }, 8000);
    }
  }

  async function refreshServerSub() {
    try {
      if (!userInfo?.id) {
        return false;
      }

      const res = await api.get("/me/subscription");

      const confirmed =
        !!res.data?.is_premium ||
        !!res.data?.active;

      setServerSubCached({
        active: !!res.data?.active,
        is_premium: !!res.data?.is_premium,
        sub_expiry: res.data?.sub_expiry ?? null,
        provider: res.data?.provider ?? null,
      });

      return confirmed;
    } catch {
      return false;
    }
  }

  function restorePendingWebCheckout() {
    const pending =
      recoverableWebCheckout;

    if (!pending) return;

    if (
      pending.buy_amount !== null &&
      pending.buy_amount !== undefined
    ) {
      setBuyAmount(
        String(pending.buy_amount)
      );
    }

    if (pending.fiat_currency) {
      setFiatCurrency(
        pending.fiat_currency
      );
    }

    if (pending.frequency) {
      setFrequency(
        pending.frequency
      );
    }

    setDestination(
      pending.destination || "strike"
    );

    setPayoutAddr(
      pending.payout_address || ""
    );

    setSmartTiming(true);

    userDisabledSmartTimingRef.current =
      false;

    if (
      pending.kind === "update" &&
      pending.plan_id
    ) {
      setEditMode(true);
      setPendingChanges(true);
    }

    setRecoverableWebCheckout(null);

    setMessage(
      serverSub.active
        ? "Your saved Smart Timing setup is restored. Review it and save your plan."
        : "Your saved Smart Timing setup is restored. Review it and continue your free trial."
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }


  async function discardPendingWebCheckout() {
    try {
      await api.delete(
        "/me/smart-timing-checkout-pending"
      );

      setRecoverableWebCheckout(null);

      setMessage(
        "Saved Smart Timing setup cleared."
      );
    } catch (e) {
      console.error(
        "[BuyPlans] could not clear pending Smart Timing setup",
        e
      );

      setMessage(
        "We couldn't clear the saved setup. Please try again."
      );
    }
  }

  async function startWebCheckout({
    kind,
    planId = null,
    entry = "plans",
  }) {
    if (!isWeb) {
      return false;
    }

    if (!userInfo?.id) {
      setMessage(
        "Your account is still loading. Please try again in a moment."
      );
      return false;
    }

    const pending = {
      version: 1,
      kind,
      plan_id: planId,

      buy_amount: Number(buyAmount),
      fiat_currency: fiatCurrency,
      frequency,

      destination,

      payout_address:
        destination === "onchain"
          ? extractAddress(payoutAddr).trim()
          : null,
    };

    try {
      await api.post(
        "/me/smart-timing-checkout-pending",
        pending
      );
    } catch (e) {
      console.error(
        "[BuyPlans] could not save web checkout state",
        e
      );

      setMessage(
        "We couldn't save your Smart Timing setup. Please try again."
      );

      return false;
    }

    afTrack("smart_timing_web_checkout_started", {
      ...planContext,
      entry,
      action: kind,
      plan_id: planId,
      product: "smart_timing_monthly_web",
      trial: true,
      price_after_trial: 1.99,
    });

    try {
      const purchaseUrl =
        buildSmartTimingWebPurchaseUrl({
          appUserId: userInfo.id,
          email: userInfo.email || null,
        });

      window.location.assign(purchaseUrl);

      return true;
    } catch (e) {
      console.error(
        "[BuyPlans] web checkout failed to start",
        e
      );

      try {
        await api.delete(
          "/me/smart-timing-checkout-pending"
        );
      } catch {}

      setMessage(
        e?.message ||
          "Could not open Smart Timing checkout. Please try again."
      );

      afTrack("smart_timing_web_checkout_failed", {
        ...planContext,
        entry,
        reason: e?.message || "unknown",
      });

      return false;
    }
  }

  async function finishPendingWebAction(pending) {
    if (!pending) {
      return "/plans?smartTiming=1";
    }

    if (
      Number(pending.user_id) !==
      Number(userInfo?.id)
    ) {
      throw new Error(
        "This checkout belongs to a different SatStacker account."
      );
    }

    const age =
      Date.now() - Number(pending.ts || 0);

    if (
      !Number.isFinite(age) ||
      age < 0 ||
      age > WEB_CHECKOUT_MAX_AGE_MS
    ) {
      throw new Error(
        "Your saved plan setup expired. Smart Timing is active, but please review your plan again."
      );
    }

    const token = await ensureFreshToken();

    if (!token) {
      throw new Error(
        "Your login expired. Please sign in again."
      );
    }

    const amount =
      Number(pending.buy_amount);

    if (
      !Number.isFinite(amount) ||
      amount < 5
    ) {
      throw new Error(
        "Smart Timing is active, but your saved plan amount is invalid. Please review the plan."
      );
    }

    const currency =
      pending.fiat_currency || null;

    const dest =
      pending.destination || "strike";

    const address =
      dest === "onchain"
        ? String(
            pending.payout_address || ""
          ).trim()
        : null;

    if (dest === "onchain") {
      if (!address) {
        throw new Error(
          "Smart Timing is active, but your Bitcoin address is missing."
        );
      }

      if (!isValidBitcoinAddress(address)) {
        throw new Error(
          "Smart Timing is active, but your Bitcoin address is invalid."
        );
      }

      await api.post(
        "/users/set-deposit-address",
        { address },
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );
    }

    const commonPayload = {
      fiat_currency: currency,
      amount_fiat: amount,

      ...(currency === "USD"
        ? { amount_usd: amount }
        : {}),

      freq:
        pending.frequency || "weekly",

      smart_timing: true,

      destination: dest,

      payout_address:
        dest === "onchain"
          ? address
          : null,
    };

    if (pending.kind === "create") {
      const res = await api.post(
        "/buyplans",
        {
          user_id: userInfo.id,
          ...commonPayload,
        },
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

      setMessage("Plan created!");
      setShowConfirm(false);

      try {
        await fetchBuyPlans();
      } catch {}

      afTrack(
        "buy_plan_created",
        {
          ...planContext,
          backend: "success",
          entry:
            "web_checkout_return",
        }
      );

      return "/plans?smartTiming=1";
    }

    if (
      pending.kind === "update" &&
      pending.plan_id
    ) {
      const idemKey =
        crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random()}`;

      const res = await api.patch(
        `/buyplans/${pending.plan_id}`,
        commonPayload,
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
            "X-Idempotency-Key":
              idemKey,
          },
        }
      );

      const requiresConfirmation =
        res?.status === 202 ||
        res?.data?.email_sent === true ||
        res?.data?.requires_confirmation ===
          true ||
        res?.data?.status ===
          "pending_confirmation";

      if (requiresConfirmation) {
        setServerPending(true);

        setMessage("");
        setShowConfirm(false);

        setEditMode(false);
        setPendingChanges(false);
        setAmountTouched(false);

        showEmail(
          "edit",
          res?.data?.detail ||
            "Check your email to confirm this change."
        );

        return "/plans?pending=1";
      }

      setMessage(
        "Smart Timing enabled!"
      );

      setShowConfirm(false);
      setEditMode(false);
      setPendingChanges(false);
      setAmountTouched(false);

      try {
        await fetchBuyPlans();
      } catch {}

      return "/plans?smartTiming=1";
    }

    return "/plans?smartTiming=1";
  }

  useEffect(() => {
    if (!isWeb) return;
    if (!userInfo?.id) return;

    const params =
      new URLSearchParams(
        location.search
      );

    if (
      params.get("webPurchase") !==
      "success"
    ) {
      return;
    }

    let cancelled = false;

    /*
    * Important:
    * Don't let the existing auto-enable effect race our web
    * checkout completion/update logic.
    */
    suppressAutoEnableRef.current = true;

    const finishWebCheckout =
      async () => {
        try {
          setActivating(true);

          setMessage(
            "Payment complete. Activating Smart Timing…"
          );

          let confirmed = false;

          // RevenueCat webhook usually arrives quickly, but don't
          // assume the browser redirect beats the webhook.
          for (
            let i = 0;
            i < 30 && !confirmed;
            i++
          ) {
            if (cancelled) return;

            confirmed =
              await refreshServerSub();

            if (confirmed) break;

            await new Promise(
              (resolve) =>
                setTimeout(resolve, 1000)
            );
          }

          if (!confirmed) {
            if (!cancelled) {
              setMessage(
                "Your checkout completed, but Smart Timing is still syncing. Please refresh this page in a few seconds."
              );
            }

            return;
          }

          if (cancelled) return;

          let pending = null;

          try {
            const pendingRes = await api.get(
              "/me/smart-timing-checkout-pending"
            );

            pending =
              pendingRes?.data?.pending || null;
          } catch (e) {
            console.warn(
              "[BuyPlans] could not load saved checkout state",
              e
            );
          }

          const nextPath =
            await finishPendingWebAction(
              pending
            );

          try {
            await api.delete(
              "/me/smart-timing-checkout-pending"
            );
          } catch (e) {
            console.warn(
              "[BuyPlans] could not clear completed checkout state",
              e
            );
          }

          const tok = getAccessToken();

          if (tok) {
            try {
              await fetchUserInfo(tok);
            } catch {}
          }

          afTrack(
            "smart_timing_web_purchase_success",
            {
              ...planContext,
              product:
                "smart_timing_monthly_web",
              price: 1.99,
              currency: "USD",
            }
          );

          if (!cancelled) {
            navigate(nextPath, {
              replace: true,
            });
          }
        } catch (e) {
          console.error(
            "[BuyPlans] web checkout completion failed",
            e
          );

          if (!cancelled) {
            /*
            * The subscription may already be active even if restoring
            * the pending plan failed. Be explicit about that.
            */
            setMessage(
              e?.message ||
                "Smart Timing is active, but we couldn't finish your plan automatically. Please review it and try again."
            );
          }

          afTrack(
            "smart_timing_web_checkout_return_failed",
            {
              ...planContext,
              reason:
                e?.message || "unknown",
            }
          );
        } finally {
          if (!cancelled) {
            setActivating(false);
          }
        }
      };

    finishWebCheckout();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isWeb,
    userInfo?.id,
    location.search,
  ]);

  async function subscribeAndSync({ showSuccessModal = true } = {}) {
    let purchaseCompleted = false;
    let subscriptionConfirmed = false;

    try {
      if (!referralOfferLoaded) {
        throw new Error(
          "We’re finishing your subscription setup. Please try again in a moment."
        );
      }

      setActivating(true);
      afTrack("smart_timing_checkout_started", {
        ...planContext,
        product: "smart_timing_monthly",
      });
      void api
        .post("/me/smart-timing-checkout-started")
        .catch((err) => {
          console.warn(
            "[BuyPlans] could not persist checkout start",
            err
          );
        });
      const ua = (navigator?.userAgent || "").toLowerCase();

      // 1) Have a fresh bearer for post-purchase server calls
      try {
        await refreshAccessToken();
      } catch {}

      if (ua.includes("android")) {
        // 2) Resolve a solid uid BEFORE touching RC
        const uid =
          (typeof window !== "undefined" && window.__USER_ID__ ? String(window.__USER_ID__) : null) ||
          (userInfo?.id != null ? String(userInfo.id) : null);
        if (!uid) {
          try { await fetchUserInfo(getAccessToken()); } catch {}
          throw new Error("Please try again in a moment — your account isn’t fully loaded yet.");
        }

        // 3) Make RC ready + guarantee a logged-in user
        await waitForRevenueCatReady(10000);

        if (
          !Purchases ||
          typeof Purchases.getOfferings !== "function"
        ) {
          throw new Error(
            "Billing not ready (RevenueCat plugin not available)."
          );
        }

        const currentUserId =
          await Purchases.getAppUserID();

        if (currentUserId !== uid) {
          await Purchases.logIn({
            appUserID: uid,
          });

          window.__RC_LOGGED__ = uid;
        }

        const offerings = await Purchases.getOfferings();

        const selectedOffering = referralEligible
          ? offerings?.all?.["smart_timing_referral"]
          : offerings?.current;

        if (
          !selectedOffering ||
          !selectedOffering.availablePackages?.length
        ) {
          throw new Error(
            referralEligible
              ? "Referral subscription offer is not available yet."
              : "No subscription packages available."
          );
        }

        const ANDROID_PRODUCT_ID =
          "smart_timing_monthly:smart-timing";

        const pkg =
          selectedOffering.availablePackages.find(
            (p) =>
              p?.product?.identifier === ANDROID_PRODUCT_ID
          ) ||
          selectedOffering.availablePackages[0];

        // 5) Purchase (Capacitor requires { aPackage })
        let result;

        try {
          afTrack("smart_timing_store_purchase_started", {
            ...planContext,
            platform: "android",
            product: pkg?.product?.identifier || ANDROID_PRODUCT_ID,
          });

          if (referralEligible) {
            const options =
              pkg?.product?.subscriptionOptions || [];

            const referralOption = options.find(
              (option) =>
                Array.isArray(option?.tags) &&
                option.tags.includes("referral")
            );

            if (!referralOption) {
              throw new Error(
                "Your 2-month referral offer is not available yet. Please try again shortly."
              );
            }

            result =
              await Purchases.purchaseSubscriptionOption({
                subscriptionOption: referralOption,
              });
          } else if (pkg) {
            result = await Purchases.purchasePackage({
              aPackage: pkg,
            });
          } else {
            result = await Purchases.purchaseProduct({
              productIdentifier: ANDROID_PRODUCT_ID,
            });
          }

          afTrack("smart_timing_store_purchase_completed", {
            ...planContext,
            platform: "android",
            product: pkg?.product?.identifier || ANDROID_PRODUCT_ID,
          });

          purchaseCompleted = true;
        } catch (e) {
          const msg = e?.message || "";
          const code = e?.code ?? e?.errorCode;

          const userCancelled =
            e?.userCancelled === true ||
            /cancel/i.test(msg) ||
            code === "PURCHASE_CANCELLED" ||
            code === "PurchaseCancelledError" ||
            code === 1;

          if (userCancelled) {
            afTrack("smart_timing_purchase_cancelled", {
              ...planContext,
              platform: "android",
            });
            return false;
          }

          throw e;
        }

        // 6) Server sync (idempotent) then refresh server view
        try {
          await syncSubToBackend();

          // FAST PATH:
          // if backend already knows about the entitlement,
          // don't wait for the polling loop at all.
          subscriptionConfirmed = await refreshServerSub();
        } catch (e) {
          console.warn(
            "[BuyPlans] post-purchase sync failed (will rely on polling)",
            e
          );
        }

        // 7) Poll backend so UI flips quickly after webhook/receipt processing
        const bearer =
          getAccessToken()

        for (
          let i = 0;
          !subscriptionConfirmed && i < 15;
          i++
        ) {
          await new Promise(r => setTimeout(r, 800));
          const resp = await fetch(
            `${import.meta.env.VITE_API_BASE_URL || "https://api.satstacker.app"}/me/subscription`,
            { headers: bearer ? { Authorization: `Bearer ${bearer}` } : {} }
          );
          if (resp.ok) {
            const s = await resp.json();
            setServerSubCached({
              active: !!s.active,
              is_premium: !!s.is_premium,
              sub_expiry: s.sub_expiry || null,
            });
            if (s?.is_premium) {
              subscriptionConfirmed = true;

              // ✅ Track purchase ONLY once premium is confirmed
              trackPurchase({
                price: Number(pkg?.product?.price ?? 1.99),
                currency: pkg?.product?.currencyCode || "USD",
                plan: pkg?.product?.identifier || "smart_timing_monthly",
              });

              afTrack("smart_timing_purchase_success", {
                ...planContext,
                product: "smart_timing_monthly",
                price: 1.99,
                currency: "USD",
              });

              break;
            }
          }
        }
        if (subscriptionConfirmed) {
          userDisabledSmartTimingRef.current = false;
          autoEnableTriedRef.current = false;

          if (showSuccessModal) {
            setShowActivated(true);
          }
        }
      } else {
        // iOS: keep your existing helper
        try {
          afTrack("smart_timing_store_purchase_started", {
            ...planContext,
            platform: "ios",
            product: "smart_timing_monthly",
          });

          if (referralEligible) {
            await waitForRevenueCatReady(10000);

            const offerings = await Purchases.getOfferings();

            const referralOffering =
              offerings?.all?.["smart_timing_referral"];

            const referralPackage =
              referralOffering?.availablePackages?.find(
                (p) => p?.identifier === "$rc_monthly"
              ) ||
              referralOffering?.availablePackages?.[0];

            if (!referralPackage) {
              throw new Error(
                "Referral subscription offer is not available yet."
              );
            }

            await Purchases.purchasePackage({
              aPackage: referralPackage,
            });
          } else {
            await buySmartTiming();
          }

          afTrack("smart_timing_store_purchase_completed", {
            ...planContext,
            platform: "ios",
            product: "smart_timing_monthly",
          });

          purchaseCompleted = true;
        } catch (e) {
          const msg  = e?.message || "";
          const code = e?.code ?? e?.errorCode;
          const userCancelled =
            e?.userCancelled === true ||
            /cancel/i.test(msg) ||
            code === "PURCHASE_CANCELLED" ||
            code === "PurchaseCancelledError" ||
            code === 1;

          if (userCancelled) {
            afTrack("smart_timing_purchase_cancelled", { ...planContext, platform: "ios" });
            return false;
          }

          afTrack("smart_timing_purchase_failed", { ...planContext, platform: "ios", reason: msg || "unknown" });
          throw e;
        }
        try {
          await syncSubToBackend();

          // FAST PATH:
          subscriptionConfirmed = await refreshServerSub();
        } catch (syncError) {
          console.warn(
            "[BuyPlans] purchase succeeded; immediate backend sync failed, waiting for webhook",
            syncError
          );
        }

        // Confirm premium before tracking (iOS can lag a moment)
        const bearer =
          getAccessToken()

        for (
          let i = 0;
          !subscriptionConfirmed && i < 15;
          i++
        ) {
          await new Promise(r => setTimeout(r, 800));
          const resp = await fetch(
            `${import.meta.env.VITE_API_BASE_URL || "https://api.satstacker.app"}/me/subscription`,
            { headers: bearer ? { Authorization: `Bearer ${bearer}` } : {} }
          );
          if (resp.ok) {
            const s = await resp.json();
            setServerSubCached({
              active: !!s.active,
              is_premium: !!s.is_premium,
              sub_expiry: s.sub_expiry || null,
            });
            if (s?.is_premium) {
              subscriptionConfirmed = true;
              break;
            }
          }
        }

        if (subscriptionConfirmed) {
          trackPurchase({
            price: 1.99,
            currency: "USD",
            plan: "smart_timing_monthly",
          });

          afTrack("smart_timing_purchase_success", {
            ...planContext,
            product: "smart_timing_monthly",
            price: 1.99,
            currency: "USD",
          });

          userDisabledSmartTimingRef.current = false;
          autoEnableTriedRef.current = false;

          if (showSuccessModal) {
            setShowActivated(true);
          }
        }
      }

      if (purchaseCompleted && subscriptionConfirmed) {
        setMessage("Smart Timing unlocked.");
        return true;
      }

      if (purchaseCompleted && !subscriptionConfirmed) {
        // The store accepted the subscription, but our backend has not
        // reflected the RevenueCat entitlement yet. Do NOT create the
        // Smart Timing plan until the backend confirms it.
        setMessage(
          "Your subscription started. We’re finishing Smart Timing activation — this should only take a few seconds."
        );

        afTrack("smart_timing_purchase_pending_sync", {
          ...planContext,
          product: "smart_timing_monthly",
        });

        // Give webhook/sync processing a couple more chances to refresh
        // the UI without making the user purchase again.
        setTimeout(() => {
          refreshServerSub().catch(() => {});
        }, 2000);

        setTimeout(() => {
          refreshServerSub().catch(() => {});
        }, 5000);

        return false;
      }

      return false;
    } catch (e) {
      console.error("[BuyPlans] subscribe failed", e);

      if (!subscribeErrorShownRef.current) {
        subscribeErrorShownRef.current = true;
        setDisabledReasonText(
          e?.message || "Subscription failed. Please try again."
        );
        setShowDisabledReason(true);

        setTimeout(() => {
          subscribeErrorShownRef.current = false;
        }, 2000);

        afTrack("smart_timing_purchase_failed", {
          ...planContext,
          reason: e?.message ?? "unknown",
        });
      }

      return false;
    } finally {
      setActivating(false);
    }
  }

  // Persist pending confirm UI across reloads / first open
  function setPendingEmail(kind /* 'edit' | 'delete' */) {
    try {
      localStorage.setItem(
        "pendingPlanConfirm",
        JSON.stringify({ kind, ts: Date.now() })
      );
    } catch {}
  }

  function clearPendingEmail() {
    try { localStorage.removeItem("pendingPlanConfirm"); } catch {}
  }

  // Show the email notice immediately & reliably (microtask) + persist
  function showEmail(kind, text) {
    setEmailNoticeText(
      text ||
      (kind === "delete"
        ? "Check your email to confirm the deletion."
        : "Check your email to confirm this change.")
    );
    Promise.resolve().then(() => {
      setShowEmailNotice(true);
      setPendingEmail(kind);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  const handleUpdatePlan = async (planId) => {
    if (strikeStatus.linked && requiresCurrencySelection && !fiatCurrency) {
      setMessage("Choose a funding currency before starting your trial.");
      return;
    }

    if (destination === "onchain") {
      const saved = await saveAddress();
      if (!saved) return;
    }

    const needsWebSmartTimingTrial =
      smartTiming &&
      !serverSub.active &&
      originalPlan &&
      !originalPlan.smart_timing &&
      isWeb;

    if (
      needsWebSmartTimingTrial
    ) {
      afTrack(
        "smart_timing_upgrade_cta_clicked",
        {
          ...planContext,
          entry: "edit_plan",
          plan_id: planId,
          platform: "web",
          trial: true,
          price_after_trial: 1.99,
        }
      );

      await startWebCheckout({
        kind: "update",
        planId,
        entry: "edit_plan",
      });

      return;
    }

    const needsSmartTimingTrial =
      smartTiming &&
      !serverSub.active &&
      originalPlan &&
      !originalPlan.smart_timing &&
      isNative();

    if (needsSmartTimingTrial) {
      afTrack("smart_timing_upgrade_cta_clicked", {
        ...planContext,
        entry: "edit_plan",
        plan_id: planId,
        trial: nativeTrialExpected,
        price_after_trial: 1.99,
      });

      suppressAutoEnableRef.current = true;

      const purchaseCompleted = await subscribeAndSync({
        showSuccessModal: false,
      });

      if (!purchaseCompleted) {
        suppressAutoEnableRef.current = false;
        return;
      }

      // Subscription succeeded — apply Smart Timing directly
      // to the existing plan.
      await actuallyUpdatePlan();
      return;
    }

    setPendingAction({
      type: "update",
      planId,
      data: {
        amount: buyAmount,
        frequency,
      },
    });

    afTrack("buy_plan_update_intent", {
      ...planContext,
      plan_id: planId,
      entry: "confirm_changes_button",
    });

    setShowConfirm(true);
  };

  const handleDeletePlan = (planId) => {
    setPendingAction({ type: "delete", planId });
    afTrack("buy_plan_delete_intent", {
      ...planContext,
      plan_id: planId,
    });
    setShowConfirm(true);
  };

  const actuallyDeletePlan = async () => {
    setSaving(true);
    suppressAutoEnableRef.current = true;
    setShowConfirm(false);
    const { planId } = pendingAction;

    // 1) Optimistically show the email notice right away
    showEmail("delete", "Check your email to confirm this deletion.");

    try {
      // refresh + retry once on 401
      const res = await withAuthRetry(async (retried) => {
        if (!retried) await ensureFreshToken();
        const token = getAccessToken();
        return api.delete(`/buyplans/${planId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      });

      const emailSent =
        res?.status === 202 ||
        res?.data?.email_sent === true ||
        res?.data?.requires_confirmation === true ||
        res?.data?.status === "pending_confirmation";

      if (emailSent) {
        setMessage("");
        setPendingChanges(false);
        setEditMode(false);
        navigate("/plans?pending=1", { replace: true });
        return;
      }

      // Not an email-confirm case → close the optimistic modal and treat as normal success
      setShowEmailNotice(false);
      clearPendingEmail();

      try { await fetchBuyPlans(); } catch {}
      setMessage("Plan deleted");
      setEditMode(false);
      setPendingChanges(false);

    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "Failed to delete plan.";

      if (status === 409) {
        setMessage("");
        setEditMode(false);
        navigate("/plans?pending=1", { replace: true });
        return;
      }

      if (status === 404) {
        setShowEmailNotice(false);
        clearPendingEmail();

        try { await fetchBuyPlans(); } catch {}
        setMessage("Plan already deleted.");
        setEditMode(false);
        setPendingChanges(false);
        return;
      }

      setShowEmailNotice(false);
      clearPendingEmail();
      console.error("Error deleting plan:", err);
      setMessage(detail);
    } finally {
      setSaving(false);
      setTimeout(() => { suppressAutoEnableRef.current = false; }, 8000);
    }
  };

  const handleChange = (setter) => (e) => {
    const value = e.target.value;
    setter(value);
    setPendingChanges(true);

    if (setter === setPayoutAddr) {
      if (destination !== "onchain") {
        setMessage("");
        return;
      }
      const cleaned = extractAddress(value).trim();
      if (!cleaned) setMessage("Deposit address is required");
      else if (!isValidBitcoinAddress(cleaned)) setMessage("Not a valid Bitcoin address");
      else setMessage("");
    }
  };

  const toggleSmartTiming = () => {
    const next = !smartTiming;
    
    // Track when user explicitly disables smart timing
    if (!next) {
      userDisabledSmartTimingRef.current = true;
    } else {
      userDisabledSmartTimingRef.current = false;
    }
    
    setSmartTiming(next);
    setPendingChanges(true);

    afTrack("smart_timing_toggled", {
      ...planContext,
      enabled: next,
      blocked: !serverSub.active, // tells you if they wanted it but couldn't
    });
  };

  useEffect(() => {
    // If the user is subscribed and creating their first plan, default Smart Timing ON.
    if (serverSub.active && !originalPlan && !smartTiming) {
      setSmartTiming(true);
    }
    // do not auto-uncheck while subscription state is loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSub.active, originalPlan?.id]);

  useEffect(() => {
    (async () => {
      try {
        const token = getAccessToken();
        if (!token || !userInfo?.id) return;

        // Prefer a slim endpoint if you have it
        try {
          const res = await api.get("/me/subscription", {
            headers: { Authorization: `Bearer ${token}` },
          });
          setServerSubCached({
            active: Boolean(res.data?.active),
            is_premium: Boolean(res.data?.is_premium),
            sub_expiry: res.data?.sub_expiry ?? null,
          });
        } catch {
          // Fallback to /me if /me/subscription isn't available
          const res2 = await api.get("/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          const exp = res2.data?.sub_expiry ? new Date(res2.data.sub_expiry) : null;
          const active = Boolean(res2.data?.is_premium) && (exp ? exp > new Date() : true);
          setServerSubCached({
            active,
            is_premium: !!res2.data?.is_premium,
            sub_expiry: res2.data?.sub_expiry ?? null,
          });
        }
      } catch {
        setServerSubCached({ active: false, is_premium: false, sub_expiry: null });
      }
    })();
  }, [userInfo?.id, subTick]);

  // restore pending “check your email” banner on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem("pendingPlanConfirm");
      if (!raw) return;
      const { kind, ts } = JSON.parse(raw);
      // optional TTL of 24h to avoid stale banners
      const fresh = !ts || (Date.now() - ts) < 24 * 60 * 60 * 1000;
      const serverPending = buyPlans?.[0]?.status === "pending_confirmation";
      if (fresh && (hasPendingFlag() || serverPending)) {
        showEmail(kind, null);
      } else {
        clearPendingEmail();
      }
    } catch {}
  }, [buyPlans, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const confirmed = params.get("confirmed");

    if (confirmed === "delete") {
      setMessage("Plan deleted");
      setShowEmailNotice(false);
      clearPendingEmail();     // ← clear persistence
      fetchBuyPlans?.();
      navigate("/plans", { replace: true });
    } else if (confirmed === "1") {
      setMessage("Changes saved!");
      setShowEmailNotice(false);
      clearPendingEmail();     // ← clear persistence
      fetchBuyPlans?.();
      navigate("/plans", { replace: true });
    } else if (params.get("confirmError") === "1") {
      setMessage("Confirmation link failed or expired.");
      // you can keep the banner or clear it here — your call:
      // clearPendingEmail();
      navigate("/plans", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const formatProofSats = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n).toLocaleString() : "—";
  };

  const formatProofDate = (value) => {
    if (!value) return "";

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) return "";

    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };

  /* ──────────────────────────────────────────────────────────
     UI
  ────────────────────────────────────────────────────────── */
  // BuyPlans.jsx  ─ add this just *above* the return statement
  const msgLower = typeof message === "string" ? message.toLowerCase() : "";
  const msgColor =
    msgLower.includes("invalid") || msgLower.includes("required")
      ? "text-red-500"
      : "text-green-500";
  const disabledUpdate = activating || !pendingChanges || addrBlocker || amountTooLow || saving;
  const disabledCreate = activating || addrBlocker || amountTooLow || saving;

  function getCreateBlockReason() {
    if (saving) return "We’re saving your previous changes. Please wait a moment.";
    if (amountTooLow) {
      return `Minimum purchase is ${formatFiat(
        5,
        fiatCurrency || "USD",
      )}.`;
    }
    if (addrBlocker) {
      if (destination === "onchain") {
        if (addressMissing) return "Enter a Bitcoin address to withdraw to.";
        if (!addressValid) return "That doesn’t look like a valid Bitcoin address.";
      }
      return "Please fix the destination details.";
    }
    return "Something prevented submitting your plan. Please try again.";
  }

  const estimateSats = Number(
    calcEstimate?.extra_sats ?? calcEstimate?.extraSats ?? NaN
  );
  const estimatePct = calcEstimate?.extra_pct != null
    ? Number(calcEstimate.extra_pct)
    : null;
  const estimateAmount = calcEstimate?.amount ?? buyAmount;
  const estimateFreq = calcEstimate?.freq ?? frequency;
  const freqLabel = (estimateFreq || frequency || "weekly") === "bi-weekly"
    ? "bi-weekly"
    : String(estimateFreq || frequency || "weekly");
  // Historical simulations can be negative or exactly zero, too.
  const hasCalcEstimate =
    Number.isFinite(estimateSats) &&
    estimatePct != null &&
    Number.isFinite(estimatePct);
  // Older calculator prefills did not identify their benchmark; avoid
  // relabeling an old saved window-open estimate as scheduled-DCA performance.
  const estimateUsesScheduledDca =
    calcEstimate?.benchmark === "scheduled_window_end";
  const signedEstimatePct =
    `${estimatePct > 0 ? "+" : ""}${Number(estimatePct).toFixed(2)}%`;
  const signedEstimateSats =
    `${estimateSats > 0 ? "+" : ""}${Math.round(estimateSats).toLocaleString()}`;
  const currentAmount = Number(buyAmount || 0);
  const monthlyBudgetLabel = (() => {
    if (!currentAmount || !Number.isFinite(currentAmount)) return null;
    if (frequency === "daily") return currentAmount * 30;
    if (frequency === "weekly") return currentAmount * 4;
    return currentAmount * 2;
  })();

  const chooseSmartTiming = () => {
    // All browser visitors select Smart Timing here and enter the same web checkout.
    if (!smartTiming) {
      userDisabledSmartTimingRef.current = false;
      setSmartTiming(true);
      setPendingChanges(true);

      afTrack("smart_timing_recommended_card_clicked", {
        ...planContext,
        entry: "recommended_plan_card",
        estimate_sats: estimateSats || null,
        estimate_pct: estimatePct,
        subscription_active: !!serverSub.active,
      });

      if (
        isWeb &&
        buyPlans.length > 0
      ) {
        setEditMode(true);
      }
    }
  };

  const chooseStandardDca = () => {
    if (smartTiming && serverSub.active) {
      toggleSmartTiming();
    } else {
      userDisabledSmartTimingRef.current = true;
      setSmartTiming(false);
      setPendingChanges(true);
      afTrack("standard_dca_selected", {
        ...planContext,
        estimate_sats: estimateSats || null,
      });
    }
  };

  const canAdvertiseAppleTrial =
    !isIOS ||
    iosIntroEligibility === "eligible";

  const createButtonText = saving
    ? "Submitting…"
    : activating
      ? "Opening subscription…"
      : smartTiming &&
        !serverSub.active &&
        (isNative() || isWeb)
        ? (
            canAdvertiseAppleTrial
              ? "Start free trial & create plan"
              : "Subscribe & create plan"
          )
        : smartTiming
          ? "Create Smart Timing plan"
          : "Create my plan";

  const updateButtonText =
    activating
      ? "Opening subscription…"
      : smartTiming &&
        !serverSub.active &&
        originalPlan &&
        !originalPlan.smart_timing &&
        (isNative() || isWeb)
        ? (
            canAdvertiseAppleTrial
              ? "Start free trial & enable Smart Timing"
              : "Subscribe & enable Smart Timing"
          )
        : "Confirm changes";

  const nativeTrialExpected =
    !isIOS ||
    iosIntroEligibility === "eligible";
  
  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto flex flex-col gap-4">

        {isWeb &&
          recoverableWebCheckout && (
            <div className="ss-card ss-card-pad border border-orange-500/30">
              <p className="text-sm font-bold text-orange-400">
                Continue Smart Timing setup
              </p>

              <p className="mt-1 text-sm text-textMuted">
                We saved the plan you were setting up so you don't have to start over.
              </p>

              {serverSub.active && (
                <p className="mt-2 text-xs text-green-400">
                  Your Smart Timing subscription is active.
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="ss-btn-primary px-4 py-2"
                  onClick={
                    restorePendingWebCheckout
                  }
                >
                  Continue setup
                </button>

                <button
                  type="button"
                  className="ss-btn-secondary px-4 py-2"
                  onClick={
                    discardPendingWebCheckout
                  }
                >
                  Start over
                </button>
              </div>
            </div>
          )}

        {buyPlans.length ? (
          <div className="flex flex-col gap-4">
            <div className="ss-card ss-card-pad">
              <p className="ss-section-title">Buy plan</p>
              <h2 className="text-xl font-bold text-white">My Plan</h2>
              <p className="text-sm text-textMuted mt-1">
                Manage your amount, cadence, destination, and Smart Timing.
              </p>
            </div>

            {/* ── Strike activation nudge ── only in manage mode, before Strike is connected ── */}
            {!strikeStatus.linked && (
              <div className="ss-card ss-card-pad border border-orange-400/40 bg-zinc-950/60">
                <p className="font-semibold text-white text-sm">Connect Strike to activate your plan</p>
                <p className="text-xs text-textMuted mt-1 leading-relaxed">
                  Your plan is saved. Connect your Strike account to start buying Bitcoin automatically.
                </p>

                {/* Yes/No question */}
                {strikeQuestion === null && (
                  <div className="mt-3">
                    <p className="text-xs text-textMuted mb-2">Do you have a Strike account?</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setStrikeQuestion("yes");
                          try { localStorage.setItem("strike_has_account", "yes"); } catch {}
                          afTrack("strike_question_answered", { answer: "yes" });
                        }}
                        className="flex-1 py-2 rounded-lg border border-zinc-600 text-xs text-white hover:border-orange-500 hover:text-orange-400 transition"
                      >
                        Yes, I have Strike
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setStrikeQuestion("no");
                          try { localStorage.setItem("strike_has_account", "no"); } catch {}
                          afTrack("strike_question_answered", { answer: "no" });
                          setShowStrikeExplainer(true);
                        }}
                        className="flex-1 py-2 rounded-lg border border-zinc-600 text-xs text-white hover:border-orange-500 hover:text-orange-400 transition"
                      >
                        No, tell me more
                      </button>
                    </div>
                  </div>
                )}

                {/* Has Strike — show Activate */}
                {strikeQuestion === "yes" && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowStrikeLinkWarning(true)}
                      disabled={strikeConnecting}
                      className="ss-btn-primary px-4 py-2 text-sm"
                    >
                      {strikeConnecting ? "Connecting…" : "Activate"}
                    </button>
                    {strikeRetryPrompt && (
                      <div className="mt-3 p-2 rounded-lg bg-zinc-900 border border-zinc-700 text-xs text-zinc-300">
                        Strike wasn't connected — the browser may have closed early.{" "}
                        <button
                          onClick={() => { setStrikeRetryPrompt(false); handleConnectStrike(); }}
                          className="text-orange-400 underline font-medium"
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* No Strike — sent to download, show return prompt */}
                {strikeQuestion === "no" && (
                  <div className="mt-3 text-xs text-textMuted">
                    <p className="mb-2">Once you've set up Strike, come back and tap Connect below.</p>
                    <button
                      type="button"
                      onClick={() => setShowStrikeLinkWarning(true)}
                      disabled={strikeConnecting}
                      className="ss-btn-primary px-4 py-2 text-sm mb-2"
                    >
                      {strikeConnecting ? "Connecting…" : "Connect Strike"}
                    </button>
                    <div>
                      <button
                        type="button"
                      onClick={() => openStrikeStore()}
                        className="underline text-zinc-500 hover:text-zinc-300 transition"
                      >
                        Open Strike download again →
                      </button>
                    </div>
                    {strikeRetryPrompt && (
                      <div className="mt-3 p-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-300">
                        Strike wasn't connected — the browser may have closed early.{" "}
                        <button
                          onClick={() => { setStrikeRetryPrompt(false); handleConnectStrike(); }}
                          className="text-orange-400 underline font-medium"
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* NEW: Funding banner — only when Strike is linked, using Strike as destination, and balance is insufficient */}
            {editMode &&
              strikeStatus.linked &&
              destination === "strike" &&
              neededFiat > 0 &&
              typeof displayBalance === "number" &&
              (displayBalance + EPS) < neededFiat && (
                <div className="ss-card ss-card-pad bg-zinc-950/40">
                  <p className="font-semibold">Add funds in Strike to start your buy plan window</p>
                  <p className="text-sm">
                    <span className="mr-3">
                      Buying Power:{" "}
                      {formatFiat(
                        displayBalance,
                        fiatCurrency || "USD",
                      )}
                    </span>

                    <span>
                      Needed:{" "}
                      {formatFiat(
                        neededFiat,
                        fiatCurrency || "USD",
                      )}
                    </span>
                  </p>
                  <p className="text-xs mt-1 opacity-90">
                    The buy plan window won't begin until your Strike account has enough {fiatCurrency || "fiat"} to cover the plan amount.
                  </p>
                    <div className="mt-2">
                    <a
                      href="https://strike.me/en"
                      target="_blank"
                      rel="noreferrer"
                      className="ss-btn-secondary px-4 py-2"
                    >
                      Open Strike
                    </a>
                  </div>
                </div>
            )}


            {showEmailNotice && (
              <div className="ss-card ss-card-pad bg-zinc-950/40">
                We sent you an email to confirm this change. Please click it to finish.
              </div>
            )}

            <div className="ss-card ss-card-pad">
              <div className="space-y-4">
                {/* Amount */}
                <div>
                  <label
                    className="block text-sm text-textMuted mb-1"
                  >
                    Amount
                    {fiatCurrency
                      ? ` (${fiatCurrency})`
                      : ""}
                  </label>
                  <input
                    type="number"
                    value={buyAmount}
                    disabled={!editMode && buyPlans.length > 0}
                    onChange={handleChange(setBuyAmount)}
                    onBlur={() => setAmountTouched(true)}
                    className="ss-input"
                  />
                  {editMode && amountTouched && parseFloat(buyAmount || 0) > 0 && amountTooLow && (
                    <p className="text-red-400 text-sm mt-1">
                      Minimum purchase is{" "}
                      {formatFiat(
                        5,
                        fiatCurrency || "USD",
                      )}
                    </p>
                  )}
                </div>
                {!buyPlans.length &&
                  availableFiatCurrencies.length > 1 && (
                  <div className="mt-4">
                    <label
                      htmlFor="funding-currency"
                      className="mb-2 block text-sm font-medium text-gray-200"
                    >
                      Funding currency
                    </label>

                    <select
                      id="funding-currency"
                      value={fiatCurrency || ""}
                      onChange={(event) => {
                        setFiatCurrency(
                          event.target.value || null,
                        );
                        setRequiresCurrencySelection(false);
                      }}
                      className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-white"
                    >
                      <option value="">
                        Select a currency
                      </option>

                      {availableFiatCurrencies.map(
                        (currency) => (
                          <option
                            key={currency}
                            value={currency}
                          >
                            {currency}
                          </option>
                        ),
                      )}
                    </select>

                    <p className="mt-2 text-xs text-gray-400">
                      Your plan will spend from this Strike balance.
                    </p>
                  </div>
                )}

                {/* Frequency */}
                <div>
                  <label className="block text-sm text-textMuted mb-1">Frequency</label>
                  <div className="flex rounded-xl overflow-hidden border border-border">
                    {["daily", "weekly", "bi-weekly"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        disabled={!editMode && buyPlans.length > 0}
                        onClick={() => {
                          if (!editMode && buyPlans.length > 0) return;
                          setFrequency(opt);
                          setPendingChanges(true);
                        }}
                        className={`flex-1 py-2 text-sm font-medium transition-colors ${
                          frequency === opt
                            ? "bg-zinc-800 text-white"
                            : "bg-zinc-900 text-textMuted hover:bg-zinc-800/70"
                        } ${!editMode && buyPlans.length > 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        {opt === "bi-weekly" ? "Bi-Weekly" : opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Smart Timing */}
                <div className="rounded-xl border border-border bg-zinc-950/40 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <p className="font-semibold text-white">Smart Timing</p>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={smartTiming}
                        onClick={subscribedServer && (editMode || !buyPlans.length) ? toggleSmartTiming : undefined}
                        disabled={!subscribedServer || (!editMode && buyPlans.length > 0)}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                          subscribedServer && (editMode || !buyPlans.length)
                            ? smartTiming
                              ? "bg-orange-500 cursor-pointer"
                              : "bg-zinc-600 cursor-pointer"
                            : smartTiming
                              ? "bg-orange-500 opacity-40 cursor-not-allowed"
                              : "bg-zinc-700 cursor-not-allowed opacity-50"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                            smartTiming ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {subscribedServer ? (
                        <span className="ss-chip">Subscribed</span>
                      ) : isNative() ? (
                        <div className="flex flex-col items-end gap-1">
                          <button
                            type="button"
                            disabled={activating}
                            onClick={() => {
                              if (activating) return;

                            afTrack("smart_timing_subscribe_clicked", {
                              funnel_id: funnelId,
                              entry: "plans_page",
                              cadence: frequency,
                              amount_fiat: Number(
                                buyAmount || 0,
                              ),
                              fiat_currency:
                                fiatCurrency || null,
                              destination,
                              smart_timing_selected: Boolean(smartTiming),
                            });

                            subscribeAndSync().catch((e) => {
                              afTrack("smart_timing_subscribe_failed", {
                                funnel_id: funnelId,
                                reason: e?.message || "unknown",
                              });
                              console.error(e);
                            });
                          }}
                          className="flex items-center gap-0.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold whitespace-nowrap"
                        >
                          {activating ? "Loading…" : <><span>Try free</span><ChevronRight size={13} strokeWidth={2.5} /></>}
                        </button>
                        <p className="text-[10px] text-textMuted text-right">
                          {referralEligible
                            ? "2 months free, then $1.99/mo"
                            : "1 month free, then $1.99/mo"}
                        </p>
                        </div>
                      ) : isWeb ? (
                        smartTiming ? (
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs font-semibold text-orange-400">
                              Selected
                            </span>

                            <p className="text-[10px] text-textMuted text-right">
                              1 month free, then $1.99/mo
                            </p>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={chooseSmartTiming}
                            className="flex items-center gap-0.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold whitespace-nowrap"
                          >
                            <span>Select</span>
                            <ChevronRight
                              size={13}
                              strokeWidth={2.5}
                            />
                          </button>
                        )
                      ) : (
                        <a
                          href="/pricing"
                          className="text-sm underline text-orange-400"
                          onClick={() => {
                            afTrack(
                              "smart_timing_view_pricing_clicked",
                              {
                                funnel_id: funnelId,
                                entry: "plans_page",
                              }
                            );
                          }}
                        >
                          View pricing
                        </a>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-textMuted mt-2">
                    Watches for price dips within your window to get more sats for the same dollars.
                  </p>
                </div>

                {/* Destination */}
                <div>
                  <label className="block text-sm text-textMuted mb-2">Destination</label>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="dest"
                        value="strike"
                        checked={destination === "strike"}
                        onChange={() => {
                          setDestination("strike");
                          setPayoutAddr("");
                          setMessage("");
                          setPendingChanges(true);
                        }}
                        disabled={!editMode && buyPlans.length > 0}
                      />
                      <span className="text-white">Keep in Strike</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="dest"
                        value="onchain"
                        checked={destination === "onchain"}
                        onChange={() => {
                          setDestination("onchain");
                          setPendingChanges(true);
                        }}
                        disabled={!editMode && buyPlans.length > 0}
                      />
                      <span className="text-white">Withdraw to my wallet</span>
                    </label>
                  </div>
                </div>

                {/* On-chain address */}
                {destination === "onchain" && (
                  <div className="space-y-2">
                    <label className="block text-sm text-textMuted">
                      Deposit Address <span className="text-textMuted/70">(on-chain)</span>
                    </label>

                    <input
                      type="text"
                      value={payoutAddr}
                      disabled={!editMode}
                      onChange={handleChange(setPayoutAddr)}
                      className="ss-input"
                      placeholder={userInfo?.payout_address || "bc1…"}
                    />

                    {payoutAddr && (editMode || buyPlans.length === 0) && !showEmailNotice && (
                      <p className={`text-sm ${addressValid ? "text-green-400" : "text-red-400"}`}>
                        {addressValid ? "✓ Looks good" : "✗ Invalid Bitcoin address"}
                      </p>
                    )}

                    {editMode && (
                      <button
                        type="button"
                        onClick={() => setScanning(true)}
                        className="ss-btn-secondary px-4 py-2 text-sm"
                      >
                        Scan QR
                      </button>
                    )}
                  </div>
                )}

                {/* Message */}
                {message && (
                  <div className={`text-sm ${msgColor}`}>
                    {typeof message === "string" ? message : JSON.stringify(message)}
                  </div>
                )}
              </div>
            </div>

            {!editMode ? (
              <div className="mt-5 flex gap-3">
                <button className="ss-btn-secondary px-4 py-2" onClick={() => { setEditMode(true); setMessage(""); }}>
                  Edit plan
                </button>
                <button
                  className="ss-btn border border-red-500/40 bg-red-950/30 text-red-200 hover:bg-red-950/50 px-4 py-2 rounded-xl"
                  onClick={() => handleDeletePlan(buyPlans[0].id)}
                >
                  Delete plan
                </button>
              </div>
            ) : (
              <div className="mt-5 flex gap-3">
                <button
                  disabled={disabledUpdate}
                  className={`px-4 py-2 rounded-xl font-semibold ${
                    disabledUpdate ? "bg-zinc-800 text-textMuted cursor-not-allowed" : "ss-btn-primary"
                  }`}
                  onClick={() => handleUpdatePlan(buyPlans[0].id)}
                >
                  {updateButtonText}
                </button>

                <button
                  className="ss-btn-secondary px-4 py-2"
                  onClick={() => {
                    if (!originalPlan) return;
                    setBuyAmount(
                      String(
                        originalPlan.amount_fiat ??
                        originalPlan.amount_usd ??
                        ""
                      )
                    );
                    setFrequency(originalPlan.freq);
                    setSmartTiming(originalPlan.smart_timing);
                    setPayoutAddr(originalPlan.payout_address || "");
                    setEditMode(false);
                    setPendingChanges(false);
                    setDestination(originalPlan.destination || "strike");
                    userDisabledSmartTimingRef.current = !originalPlan.smart_timing;
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Compact intro */}
            <div className="ss-card ss-card-pad border border-zinc-800 bg-zinc-950/50">
              <p className="ss-section-title">Buy plan</p>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-white leading-tight">Create your plan</h2>
                  <p className="text-sm text-textMuted mt-1 leading-relaxed">
                    {hasCalcEstimate ? (
                      <>
                        Your calculator preview showed{" "}
                        <span className="text-orange-400 font-bold">
                          {signedEstimatePct}{" "}
                          {estimateUsesScheduledDca
                            ? "vs scheduled DCA"
                            : "in its selected historical period"}
                        </span>
                        . Historical simulations do not predict future results.
                      </>
                    ) : (
                      <>Choose an amount, cadence, and plan type.</>
                    )}
                  </p>
                </div>
                {hasCalcEstimate && (
                  <div className="shrink-0 rounded-2xl border border-orange-500/20 bg-orange-500/10 px-3 py-2 text-right">
                    <div className="text-[10px] uppercase tracking-widest text-orange-300/80">Historical example</div>
                    <div className="text-lg font-black text-orange-400 tabular-nums">
                      {signedEstimatePct}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Strike connected confirmation */}
            {strikeStatus.linked && (
              <div className="ss-card ss-card-pad border border-green-500/20 bg-zinc-950/60">
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <span>✓</span>
                  <span className="font-medium">Strike connected</span>
                  <span className="text-textMuted text-xs">— your plan is ready to run once saved</span>
                </div>
              </div>
            )}

            {/* Funding banner */}
            {strikeStatus.linked &&
              destination === "strike" &&
              neededFiat > 0 &&
              typeof displayBalance === "number" &&
              (displayBalance + EPS) < neededFiat && (
                <div className="ss-card ss-card-pad bg-zinc-950/40">
                  <p className="font-semibold">Add funds in Strike to start your buy plan window</p>
                  <p className="text-sm">
                    <span className="mr-3">
                      Buying Power:{" "}
                      {formatFiat(
                        displayBalance,
                        fiatCurrency || "USD",
                      )}
                    </span>

                    <span>
                      Needed:{" "}
                      {formatFiat(
                        neededFiat,
                        fiatCurrency || "USD",
                      )}
                    </span>
                  </p>
                  <div className="mt-2">
                    <a
                      href="https://strike.me/en"
                      target="_blank"
                      rel="noreferrer"
                      className="ss-btn-secondary px-4 py-2"
                    >
                      Open Strike
                    </a>
                  </div>
                </div>
            )}

            {/* Amount, cadence, and plan type */}
            <div className="ss-card ss-card-pad">
              <div className="space-y-5">
                <div>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <label className="block text-sm text-textMuted">Amount per buy</label>
                    {monthlyBudgetLabel != null && (
                      <span className="text-xs text-zinc-500">
                        ≈ ${Math.round(monthlyBudgetLabel).toLocaleString()}/mo
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 text-lg">$</span>
                    <input
                      type="number"
                      placeholder="25"
                      value={buyAmount}
                      onChange={handleChange(setBuyAmount)}
                      onBlur={() => setAmountTouched(true)}
                      className="ss-input pl-8 text-lg font-semibold"
                    />
                  </div>
                  {amountTouched && parseFloat(buyAmount || 0) > 0 && amountTooLow && (
                    <p className="text-red-400 text-sm mt-1">
                      Minimum purchase is{" "}
                      {formatFiat(
                        5,
                        fiatCurrency || "USD",
                      )}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-textMuted mb-2">Cadence</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: "daily", label: "Daily", sub: "Every day" },
                      { value: "weekly", label: "Weekly", sub: "Every week" },
                      { value: "bi-weekly", label: "Bi-weekly", sub: "Every 2 weeks" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setFrequency(opt.value);
                          setPendingChanges(true);
                        }}
                        className={`rounded-xl border px-2 py-3 text-center transition-colors ${
                          frequency === opt.value
                            ? "bg-zinc-900 border-zinc-500 text-white"
                            : "bg-zinc-950 border-border text-textMuted hover:border-zinc-600 hover:text-white"
                        }`}
                      >
                        <span className="block text-sm font-bold">{opt.label}</span>
                        <span className={`block text-[11px] mt-0.5 ${frequency === opt.value ? "text-zinc-400" : "text-zinc-600"}`}>
                          {opt.sub}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-1">
                        Timing
                      </p>
                      <h3 className="text-lg font-bold text-white">
                        Choose how buys run
                      </h3>
                    </div>
                    {hasCalcEstimate && (
                      <span className="rounded-full bg-orange-500/10 border border-orange-500/20 px-3 py-1 text-xs font-bold text-orange-400">
                        {signedEstimateSats} sats in the simulation
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={chooseSmartTiming}
                      disabled={activating}
                      className={`w-full text-left rounded-2xl border p-4 transition ${
                        smartTiming
                          ? "border-orange-400/80 bg-zinc-900 shadow-[0_0_0_1px_rgba(251,146,60,0.08)]"
                          : "border-orange-500/25 bg-zinc-950/60 hover:border-orange-400/50"
                      } ${activating ? "opacity-70 cursor-wait" : "cursor-pointer"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-bold">
                              Smart Timing DCA
                            </span>

                            <span className="rounded-full border border-orange-500/15 bg-orange-500/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-orange-300">
                              Recommended
                            </span>
                          </div>

                          <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                            Looks for favorable Bitcoin prices within each scheduled
                            buy window, without changing your recurring budget.
                          </p>

                          {recentSmartTimingWin && (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();

                                afTrack("smart_timing_recent_result_clicked", {
                                  ...planContext,
                                  more_btc_pct: recentSmartTimingWin.more_btc_pct,
                                });

                                setShowRecentResult(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowRecentResult(true);
                                }
                              }}
                              className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-orange-400 hover:text-orange-300 transition cursor-pointer"
                            >
                              <span>See a recent positive example</span>
                              <ChevronRight size={13} strokeWidth={2.5} />
                            </div>
                          )}

                          {smartTiming &&
                            !serverSub.active &&
                            isNative() && (
                              <p className="text-xs text-zinc-400 mt-2 text-center leading-relaxed">

                                {isIOS &&
                                iosIntroEligibility !== "eligible" ? (
                                  <>
                                    <span className="font-semibold text-zinc-200">
                                      $1.99/month.
                                    </span>{" "}
                                    Apple will confirm the final price before purchase.
                                    Cancel anytime.
                                  </>
                                ) : (
                                  <>
                                    <span className="font-semibold text-zinc-200">
                                      $0 today.
                                    </span>{" "}
                                    {referralEligible
                                      ? "2 months free, then $1.99/month."
                                      : "1 month free, then $1.99/month."}
                                    {" "}Cancel anytime.
                                  </>
                                )}

                              </p>
                            )}
                        </div>
                        <div
                          className={`mt-1 h-6 w-6 rounded-full border flex items-center justify-center shrink-0 transition ${
                            smartTiming
                              ? "border-orange-400 bg-transparent"
                              : "border-zinc-600"
                          }`}
                        >
                          {smartTiming && (
                            <div className="h-2.5 w-2.5 rounded-full bg-orange-400" />
                          )}
                        </div>
                      </div>
                      {!serverSub.active && (isNative() || isWeb) && (
                        <div className="mt-3">
                          <span className="text-xs font-semibold text-orange-300">
                            {smartTiming
                              ? "Smart Timing selected"
                              : "Select Smart Timing"}
                          </span>
                        </div>
                      )}

                    </button>

                    <button
                      type="button"
                      onClick={chooseStandardDca}
                      className={`w-full text-left rounded-2xl border p-4 transition ${
                        !smartTiming
                          ? "border-zinc-500 bg-zinc-900/80"
                          : "border-border bg-zinc-950/40 hover:border-zinc-600"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-bold">Standard DCA</span>
                            <span className="rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700 text-[10px] font-bold px-2 py-0.5">
                              Free
                            </span>
                          </div>
                          <p className="text-sm text-textMuted mt-1 leading-relaxed">
                            Buys automatically at the scheduled time.
                          </p>
                        </div>
                        <div
                          className={`mt-1 h-6 w-6 rounded-full border flex items-center justify-center shrink-0 ${
                            !smartTiming
                              ? "border-zinc-300"
                              : "border-zinc-600"
                          }`}
                        >
                          {!smartTiming && (
                            <div className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Destination */}
            <div className="ss-card ss-card-pad">
              <label className="block text-sm text-textMuted mb-3">Destination</label>

              <div className="space-y-2">
                <label className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${destination === "strike" ? "border-zinc-500 bg-zinc-900" : "border-border bg-zinc-950/40"}`}>
                  <input
                    type="radio"
                    name="dest_create"
                    value="strike"
                    checked={destination === "strike"}
                    onChange={() => {
                      setDestination("strike");
                      setPayoutAddr("");
                      setMessage("");
                      setPendingChanges(true);
                    }}
                  />
                  <div>
                    <span className="block text-white font-semibold">Keep in Strike</span>
                    <span className="block text-xs text-textMuted">Simplest setup. SatStacker never holds your Bitcoin.</span>
                  </div>
                </label>

                <label className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${destination === "onchain" ? "border-zinc-500 bg-zinc-900" : "border-border bg-zinc-950/40"}`}>
                  <input
                    type="radio"
                    name="dest_create"
                    value="onchain"
                    checked={destination === "onchain"}
                    onChange={() => {
                      setDestination("onchain");
                      setPendingChanges(true);
                    }}
                  />
                  <div>
                    <span className="block text-white font-semibold">Withdraw to my wallet</span>
                    <span className="block text-xs text-textMuted">Send bought Bitcoin to your own on-chain address.</span>
                  </div>
                </label>
              </div>

              {destination === "onchain" && (
                <div className="space-y-2 mt-4">
                  <label className="block text-sm text-textMuted">
                    Bitcoin address <span className="text-textMuted/70">(on-chain)</span>
                  </label>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={payoutAddr}
                      onChange={handleChange(setPayoutAddr)}
                      className="ss-input flex-1"
                      placeholder="bc1…"
                    />
                    <button
                      type="button"
                      className="ss-btn-secondary px-4 py-2 text-sm"
                      onClick={() => setScanning(true)}
                    >
                      Scan QR
                    </button>
                  </div>

                  {payoutAddr && (
                    <p className={`text-sm ${addressValid ? "text-green-400" : "text-red-400"}`}>
                      {addressValid ? "✓ Looks good" : "✗ Invalid Bitcoin address"}
                    </p>
                  )}
                </div>
              )}

              {message && (
                <div className={`text-sm mt-4 ${msgColor}`}>
                  {typeof message === "string" ? message : JSON.stringify(message)}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="ss-card ss-card-pad sticky bottom-4 z-10 shadow-2xl">

              {referralEligible &&
                smartTiming &&
                !serverSub.active &&
                isNative() &&
                (!isIOS || iosIntroEligibility === "eligible") && (
                  <div className="mb-3 rounded-xl border border-orange-500/20 bg-orange-500/5 px-3 py-2 text-center">
                    <p className="text-xs font-bold text-orange-400">
                      Referral offer unlocked
                    </p>
                    <p className="mt-0.5 text-[11px] text-zinc-400">
                      You get 2 months of Smart Timing free instead of 1.
                    </p>
                  </div>
                )}

              <button
                onClick={handleCreatePlan}
                  disabled={disabledCreate}
                className={`w-full py-3 rounded-xl font-black ${
                  disabledCreate ? "bg-zinc-800 text-textMuted cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white"
                }`}
              >
                {createButtonText}
              </button>

              {disabledCreate && (
                <p className="text-xs text-textMuted mt-3 text-center">
                  {getCreateBlockReason()}
                </p>
              )}

              {smartTiming &&
                !serverSub.active &&
                isNative() && (
                  <p className="text-xs text-zinc-400 mt-2 text-center leading-relaxed">
                    {isIOS &&
                    iosIntroEligibility !== "eligible" ? (
                      <>
                        <span className="font-semibold text-zinc-200">
                          $1.99/month.
                        </span>{" "}
                        Apple will confirm the final price before purchase.
                        {" "}Cancel anytime.
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-zinc-200">
                          $0 today.
                        </span>{" "}
                        {referralEligible
                          ? "2 months free, then $1.99/month."
                          : "1 month free, then $1.99/month."}
                        {" "}Cancel anytime.
                      </>
                    )}
                  </p>
                )}

              {smartTiming && serverSub.active && (
                <p className="text-xs text-zinc-500 mt-2 text-center">
                  Smart Timing is included with your active subscription.
                </p>
              )}

              {!smartTiming && (
                <p className="text-xs text-zinc-500 mt-2 text-center">
                  Standard timing is free.
                </p>
              )}

              <p className="text-xs text-textMuted mt-3 text-center leading-relaxed">
                {!strikeStatus.linked
                  ? "After saving, you’ll connect Strike to activate buys."
                  : "Buys execute through Strike. You can pause or change this anytime."}
              </p>
            </div>
          </div>
        )}

        {scanning && (
          <QRScannerModal
            onScan={(msg) => {
              const cleaned = extractAddress(msg);
              setPayoutAddr(cleaned);
              setScanning(false);

              // Always mark the form dirty so buttons enable, even in “create” mode
              setPendingChanges(true);
            }}
            onClose={() => setScanning(false)}
          />
        )}
        {activating && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(2px)"
            }}
            aria-busy="true"
            aria-live="polite"
          >
            <div className="rounded-lg bg-black/80 text-white px-6 py-4 text-center">
              <div className="mb-2 text-lg font-semibold">
                Activating your subscription…
              </div>

              <div className="text-sm opacity-80">
                {isWeb
                  ? "Please keep this page open while we confirm your subscription."
                  : "Please don’t close the app or navigate away."}
              </div>
            </div>
          </div>
        )}
        <ConfirmModal
          isOpen={showConfirm}
          title={
            pendingAction?.type === "update"
              ? "Confirm Plan Update"
              : pendingAction?.type === "delete"
              ? "Confirm Delete"
              : "Confirm Buy Plan"
          }
          message={
            pendingAction?.type === "delete" ? (
              <>
                <p className="text-center">Are you sure you want to delete your current buy plan?</p>
                <p className="text-sm text-red-400 text-center">This action cannot be undone.</p>
              </>
            ) : (
              <>
                <p className="text-center text-lg font-semibold">
                  Buy ${pendingAction?.data?.amount} of BTC every {pendingAction?.data?.frequency}
                </p>

                <p className="text-center text-sm text-textMuted mt-2">
                  Funds come from your connected Strike account.
                </p>

                <p className="text-center text-sm text-textMuted mt-1">
                  You can pause or change this anytime.
                </p>
              </>
            )
          }
          onCancel={() => setShowConfirm(false)}
          onConfirm={() => {
            if (pendingAction?.type === "update") { setShowConfirm(false); return actuallyUpdatePlan(); }
            if (pendingAction?.type === "delete") return actuallyDeletePlan();
            afTrack("buy_plan_confirm_clicked", {
              ...planContext,
              action: pendingAction?.type,
              plan_id: pendingAction?.planId ?? buyPlans?.[0]?.id ?? null,
            });
            return actuallyCreatePlan();
          }}
          confirmText={saving ? "Submitting…" : "Confirm"}
          confirmDisabled={saving}
        />
        <ConfirmModal
          isOpen={showEmailNotice}
          title="One more step!"
          message={
            <p>
              {emailNoticeText || "We've sent a confirmation link to your e-mail. Please click it to finish."}
            </p>
          }
          onConfirm={() => setShowEmailNotice(false)}
          onCancel={() => setShowEmailNotice(false)}
          confirmText="OK"
          hideCancel
        />
        <ConfirmModal
          isOpen={showDisabledReason}
          title="Can’t submit yet"
          message={<p>{disabledReasonText}</p>}
          onConfirm={() => setShowDisabledReason(false)}
          onCancel={() => setShowDisabledReason(false)}
          confirmText="OK"
          hideCancel
        />
        <ConfirmModal
          isOpen={showActivated}
          title="Subscription Activated"
          message={<p>Your Smart Timing features are now unlocked.</p>}
          onConfirm={() => setShowActivated(false)}
          onCancel={() => setShowActivated(false)}
          confirmText="Got it"
          hideCancel
        />

      {/* NEW: Always-visible note clarifying the window behavior */}
      <p className="text-xs text-zinc-400 mt-2">
        Note: your buy plan won’t actually execute until your Strike account is connected and funded
        {destination === "strike" ? " for this plan’s amount." : "."}
      </p>

      {/* Public, anonymized positive completed-window example. */}
      {showRecentResult && recentSmartTimingWin && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm px-4 pb-6"
          onClick={() => setShowRecentResult(false)}
        >
          <div
            className="w-full max-w-sm bg-zinc-900 border border-zinc-700 rounded-2xl p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end mb-1">
              <button
                type="button"
                onClick={() => setShowRecentResult(false)}
                className="text-zinc-500 hover:text-zinc-300 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="text-center">
              <p className="text-lg font-bold text-white">
                A recent Smart Timing example
              </p>
              {recentSmartTimingWin.completed_at && (
                <p className="mt-1 text-xs text-zinc-500">
                  Anonymized completed window ·{" "}
                  {formatProofDate(recentSmartTimingWin.completed_at)}
                </p>
              )}
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-zinc-500">Smart Timing</span>
                <span className="text-sm font-semibold text-white">
                  {formatProofSats(recentSmartTimingWin.smart_sats)} sats
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-zinc-500">Scheduled DCA</span>
                <span className="text-sm font-semibold text-zinc-200">
                  {formatProofSats(recentSmartTimingWin.scheduled_sats)} sats
                </span>
              </div>
              <div className="border-t border-zinc-800 pt-4 text-center">
                <div className="text-2xl font-bold text-orange-400">
                  +{Number(recentSmartTimingWin.more_btc_pct).toFixed(2)}%
                </div>
                <p className="mt-1 text-sm text-zinc-300">
                  +{formatProofSats(recentSmartTimingWin.extra_sats)} sats vs scheduled DCA
                </p>
                <p className="mt-3 text-xs text-zinc-500 leading-relaxed">
                  This is one positive completed-window example, not an average
                  or a prediction. Your results can be positive or negative.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowRecentResult(false)}
              className="mt-6 w-full rounded-xl bg-zinc-800 py-3 text-sm font-semibold text-white hover:bg-zinc-700 transition"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Strike link warning modal */}
      {showStrikeLinkWarning && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm px-4 pb-6">
          <div className="w-full max-w-sm bg-zinc-900 border border-zinc-700 rounded-2xl p-6 shadow-xl">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setShowStrikeLinkWarning(false)}
                className="text-zinc-500 hover:text-zinc-300 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex flex-col items-center text-center mb-5">
              <p className="text-lg font-bold text-white">Linking your Strike account</p>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-6">
              You'll be taken to a browser window to sign in to Strike and approve access. Once done, come back to SatStacker and your plan will activate automatically.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowStrikeLinkWarning(false);
                handleConnectStrike();
              }}
              className="w-full py-3 rounded-xl bg-orange-500 text-white font-bold text-sm mb-3"
            >
              Got it, let’s go →
            </button>
            <button
              type="button"
              onClick={() => setShowStrikeLinkWarning(false)}
              className="w-full py-2 text-xs text-zinc-500 hover:text-zinc-300 transition"
            >
              Not right now
            </button>
          </div>
        </div>
      )}

      {/* Strike explainer modal */}
      {showStrikeExplainer && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm px-4 pb-6">
          <div className="w-full max-w-sm bg-zinc-900 border border-zinc-700 rounded-2xl p-6 shadow-xl">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setShowStrikeExplainer(false)}
                className="text-zinc-500 hover:text-zinc-300 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex flex-col items-center text-center mb-5">
              <p className="text-lg font-bold text-white">What is Strike?</p>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-3">
              Strike is a free Bitcoin wallet and payment app. SatStacker connects to it to execute your buys. SatStacker never holds your money or Bitcoin directly.
            </p>
            <p className="text-sm text-zinc-300 leading-relaxed mb-6">
              Once you’ve downloaded Strike and created a free account, add funds via bank transfer or debit card. Then come back here and tap Connect Strike to activate your plan.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowStrikeExplainer(false);
                afTrack("strike_explainer_cta", { entry: "plans" });
                openStrikeStore();
              }}
              className="w-full py-3 rounded-xl bg-orange-500 text-white font-bold text-sm mb-3"
            >
              Got it — take me to Strike →
            </button>
            <button
              type="button"
              onClick={() => setShowStrikeExplainer(false)}
              className="w-full py-2 text-xs text-zinc-500 hover:text-zinc-300 transition"
            >
              I’ll do this later
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}